/* eslint-disable max-lines */

import { AvatarType } from '@/pages/avatar/common';
import { AvatarTypeToRecordingTimes } from '@/pages/avatar/common/consts';
import { SupportedLanguage } from '@/utils/types';
import styled from '@emotion/styled';

const TermsUrl = 'https://www.d-id.com/studio-end-user-license-agreement/';

const TermsLine = styled.a`
    color: ${({ theme }) => theme.colors.links.main};

    &:hover {
        text-decoration: none;
    }

    &:focus {
        outline: none;
    }
`;

export const consentContentByLanguage: Record<SupportedLanguage, React.ReactNode> = {
    english: (
        <>
            I hereby confirm that the voice recorded is mine or that I have all the necessary rights to submit the voice recording and I
            agree to the provisions in D-ID's{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Terms of Use
            </TermsLine>{' '}
            for my user submission.
        </>
    ),
    spanish: (
        <>
            Por la presente confirmo que la voz grabada es mía, o que tengo todos los derechos necesarios para enviar la grabación de voz, y
            acepto las disposiciones en los{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Términos de Uso
            </TermsLine>{' '}
            de D-ID para mi Envío
        </>
    ),
    italian: (
        <>
            Con la presente confermo che la voce registrata è mia, o che ho tutti i diritti necessari per inviare la registrazione vocale, e
            accetto le disposizioni nei{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Termini d'Uso
            </TermsLine>{' '}
            di D-ID per il mio Inoltro
        </>
    ),
    swedish: (
        <>
            Jag bekräftar härmed att den inspelade rösten är min, eller att jag har alla nödvändiga rättigheter att skicka in
            röstinspelningen, och jag godkänner bestämmelserna i era{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Användarvillkor
            </TermsLine>{' '}
            för mitt Användarbidrag
        </>
    ),
    german: (
        <>
            Hiermit bestätige ich, daß die aufgezeichnete Stimme meine eigene ist, oder dass ich alle notwendigen Rechte besitze, um die
            Sprachaufnahme einzureichen, und ich stimme den Bestimmungen in den{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Nutzungsbedingungen
            </TermsLine>{' '}
            von D-ID für meine "Einreichung" zu.
        </>
    ),
    french: (
        <>
            Je confirme par la présente que la voix enregistrée est la mienne, ou que je possède tous les droits nécessaires pour soumettre
            l'enregistrement vocal, et j'accepte les dispositions des{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Conditions d'Utilisation
            </TermsLine>{' '}
            de D-ID pour mon “Dépôt”
        </>
    ),
    dutch: (
        <>
            Hierbij bevestig ik dat de opgenomen stem de mijne is, of dat ik alle benodigde rechten heb om de stemopname in te dienen, en ik
            ga akkoord met de bepalingen in de{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Gebruiksvoorwaarden
            </TermsLine>{' '}
            van D-ID voor mijn "Inzending".
        </>
    ),
    filipino: (
        <>
            Kinukumpirma ko na ang naitalang boses ay akin, o na mayroon akong lahat ng kinakailangang karapatan upang isumite ang
            pag-record ng boses, at sumasang-ayon ako sa mga probisyon sa Mga Tuntunin ng{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Paggamit
            </TermsLine>{' '}
            ng D-ID para sa aking "Pagsusumite".
        </>
    ),
    portuguese: (
        <>
            Confirmo que a voz gravada é minha, ou que tenho todos os direitos necessários para submeter a gravação de voz, e concordo com
            as disposições nos{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Termos de Uso
            </TermsLine>{' '}
            da D-ID para minha "Submissão".
        </>
    ),
    turkish: (
        <>
            İşbu belgeyle kaydedilen sesin benim olduğunu veya ses kaydını sunmak için gerekli tüm haklara sahip olduğumu onaylarım ve
            "Gönderim" im için D-ID{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Kullanım Koşulları'ndaki
            </TermsLine>{' '}
            hükümleri kabul ederim.
        </>
    ),
    czech: (
        <>
            Tímto potvrzuji, že nahrávaný hlas je můj, nebo že mám všechna potřebná práva k odeslání hlasového záznamu, a souhlasím s{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                ustanoveními ve Vše
            </TermsLine>{' '}
        </>
    ),
    croatian: (
        <>
            Ovim potvrđujem da je snimljeni glas moj, ili da imam sva potrebna prava za podnošenje glasovne snimke, i slažem se s odredbama
            u{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Uvjetima korištenja
            </TermsLine>{' '}
            D-ID-a za moju "Predaju".
        </>
    ),
    indonesian: (
        <>
            Dengan ini saya mengonfirmasi bahwa suara yang direkam adalah milik saya, atau saya memiliki semua hak yang diperlukan untuk
            mengirimkan rekaman suara, dan saya setuju dengan ketentuan dalam{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Syarat Penggunaan
            </TermsLine>{' '}
            D-ID untuk "Pengajuan" saya.
        </>
    ),
    polish: (
        <>
            Niniejszym potwierdzam, że nagrany głos jest moim, lub że mam wszystkie niezbędne prawa do przesłania nagrania głosowego, i
            zgadzam się z postanowieniami w{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Regulaminie
            </TermsLine>{' '}
            D-ID dotyczącym mojego Przesłania.
        </>
    ),
    romanian: (
        <>
            Prin prezenta confirm că vocea înregistrată este a mea, sau că am toate drepturile necesare pentru a trimite înregistrarea
            vocală, și sunt de acord cu prevederile din Termenii de{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Utilizare ai
            </TermsLine>{' '}
            D-ID pentru "Trimiterea" mea.
        </>
    ),
    danish: (
        <>
            Jeg bekræfter herved, at den optagede stemme er min, eller at jeg har alle de nødvendige rettigheder til at indsende
            stemmeoptagelsen, og jeg er enig i{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                bestemmelserne i
            </TermsLine>{' '}
            D-IDs Brugsvilkår for min "Indsendelse".
        </>
    ),
    slovak: (
        <>
            Týmto potvrdzujem, že nahraný hlas je môj, alebo že mám všetky potrebné práva na odovzdanie hlasového záznamu, a súhlasím s
            ustanoveniami v{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Podmienkach používania
            </TermsLine>{' '}
            D-ID pre moje "Predloženie".
        </>
    ),
    malay: (
        <>
            Saya dengan ini mengesahkan bahawa suara yang dirakam adalah milik saya, atau saya mempunyai semua hak yang diperlukan untuk
            menghantar rakaman suara, dan saya bersetuju dengan peruntukan dalam{' '}
            <TermsLine target="_blank" href={TermsUrl}>
                Terma Penggunaan
            </TermsLine>{' '}
            D-ID untuk "Penyerahan" saya.
        </>
    ),
};

export const recordTextByLanguage: Record<SupportedLanguage, string> = {
    english: `One morning, I discovered a colorful bird singing outside my window. We became fast friends, sharing melodies daily. Wishing to
            harmonize but recognizing our different voices, I hoped for a bit of magic with a wish into the breeze. 
            To my surprise, by the next day, we were singing in perfect harmony, our songs delighting everyone. This experience taught me
            about the power of sharing and unity's beauty.
            Learning that a little magic and heart could merge different voices into beauty, we continued our harmonious songs, spreading
            joy and underscoring that friendship and harmony embody true magic.`,
    spanish: `Una mañana descubrí un pájaro colorido cantando fuera de mi ventana. Nos hicimos amigos rápidamente y compartíamos melodías a
            diario. Deseando armonizar pero reconociendo nuestras diferentes voces, esperaba un poco de magia con un deseo en la brisa. Para
            mi sorpresa, al día siguiente cantábamos en perfecta armonía y nuestras canciones deleitaba a todos. Esta experiencia me enseñó
            sobre el poder de compartir y la belleza de la unidad. Al aprender que un poco de magia y corazón podían fusionar diferentes
            voces en belleza, continuamos con nuestras canciones armoniosas, difundiendo alegría y subrayando que la amistad y la armonía
            encarnan la verdadera magia`,
    italian: `Una mattina ho scoperto un uccello colorato che cantava fuori dalla mia finestra. Siamo diventati subito amici, condividendo
            melodie ogni giorno. Desiderando armonizzare ma riconoscendo le nostre diverse voci, speravo in un po' di magia con un augurio
            nella brezza. Con mia sorpresa, il giorno successivo cantavamo in perfetta armonia, le nostre canzoni deliziavano tutti. Questa
            esperienza mi ha insegnato il potere della condivisione e la bellezza dell'unità. Imparando che un po’ di magia e di cuore
            possono fondere voci diverse nella bellezza, abbiamo continuato le nostre canzoni armoniose, diffondendo gioia e sottolineando
            che l’amicizia e l’armonia incarnano la vera magia`,
    swedish: `En morgon upptäckte jag en färgglad fågel som sjöng utanför mitt fönster. Vi blev snabba vänner och delade melodier dagligen.
            Jag ville harmonisera men kände igen våra olika röster och hoppades på lite magi med en önskan in i vinden. Till min förvåning
            sjöng vi nästa dag i perfekt harmoni, våra sånger glädde alla. Den här upplevelsen lärde mig om kraften i att dela och enhetens
            skönhet. När vi lärde oss att lite magi och hjärta kunde smälta samman olika röster till skönhet, fortsatte vi våra harmoniska
            sånger, spred glädje och understryker att vänskap och harmoni förkroppsligar sann magi.`,
    german: `Eines Morgens entdeckte ich einen bunten Vogel, der draußen vor meinem Fenster sang. Wir wurden schnell Freunde und teilten
            täglich Melodien. In der Hoffnung auf Harmonie, aber in Anerkennung unserer unterschiedlichen Stimmen, wünschte ich mir ein
            wenig Magie mit einem Wunsch in der Brise. Zu meiner Überraschung sangen wir am nächsten Tag in perfekter Harmonie, unsere
            Lieder erfreuten alle. Diese Erfahrung lehrte mich die Kraft des Teilens und die Schönheit der Einheit. In der Erkenntnis, dass
            ein wenig Magie und Herz unterschiedliche Stimmen in Schönheit vereinen können, setzten wir unsere harmonischen Lieder fort und
            verbreiteten Freude und betonten, dass Freundschaft und Harmonie wahre Magie verkörpern.`,
    french: `Un matin, j'ai découvert un oiseau coloré chantant à l'extérieur de ma fenêtre. Nous sommes rapidement devenus amis, partageant
            des mélodies quotidiennement. Souhaitant harmoniser mais reconnaissant nos voix différentes, j'espérais un peu de magie avec un
            vœu dans la brise. À ma surprise, le lendemain, nous chantions en parfaite harmonie, nos chansons ravissant tout le monde. Cette
            expérience m'a appris le pouvoir du partage et la beauté de l'unité. Apprenant qu'un peu de magie et de cœur pouvaient fusionner
            des voix différentes en beauté, nous avons continué nos chansons harmonieuses, diffusant de la joie et soulignant que l'amitié
            et l'harmonie incarnent la vraie magie.`,
    dutch: `Op een ochtend ontdekte ik een kleurrijke vogel die buiten mijn raam zong. We werden snel vrienden en deelden dagelijks
            melodieën. Hoewel ik hoopte te harmoniseren maar onze verschillende stemmen erkende, wenste ik stiekem op een beetje magie in de
            bries. Tot mijn verbazing zongen we de volgende dag in perfecte harmonie, onze liedjes vermaakten iedereen. Deze ervaring leerde
            me over de kracht van delen en de schoonheid van eenheid. Lerend dat een beetje magie en hart verschillende stemmen in
            schoonheid kunnen samenvoegen, vervolgden we onze harmonieuze liedjes, vreugde verspreidend en benadrukkend dat vriendschap en
            harmonie de ware magie belichamen.`,
    filipino: `Isang umaga, natuklasan ko ang isang makulay na ibon na umaawit sa labas ng aking bintana. Mabilis kaming naging magkaibigan,
            nagbabahagi ng mga himig araw-araw. Nagnanais na magkasundo ngunit kinikilala ang aming magkaibang tinig, umaasa ako sa kaunting
            mahika sa pamamagitan ng isang kahilingan sa simoy. Sa aking sorpresa, kinabukasan, kami ay umaawit sa perpektong pagkakasundo,
            ang aming mga awitin ay nagpapasaya sa lahat. Ang karanasang ito ay nagturo sa akin tungkol sa kapangyarihan ng pagbabahagi at
            ang kagandahan ng pagkakaisa. Natutunan na ang kaunting mahika at puso ay maaaring pagsamahin ang iba't ibang tinig sa
            kagandahan, ipinagpatuloy namin ang aming mga awiting may pagkakasundo, nagpapalaganap ng kagalakan at binibigyang diin na ang
            pagkakaibigan at pagkakasundo ay kumakatawan sa tunay na mahika.`,
    portuguese: `Numa manhã, descobri um pássaro colorido cantando fora da minha janela. Tornamo-nos rapidamente amigos, partilhando melodias
            diariamente. Desejando harmonizar, mas reconhecendo as nossas vozes diferentes, esperava por um pouco de magia com um desejo na
            brisa. Para minha surpresa, no dia seguinte, estávamos cantando em perfeita harmonia, as nossas canções agradando a todos. Esta
            experiência ensinou-me sobre o poder de partilhar e a beleza da unidade. Aprendendo que um pouco de magia e coração podem unir
            vozes diferentes em beleza, continuámos as nossas canções harmoniosas, espalhando alegria e sublinhando que a amizade e a
            harmonia encarnam a verdadeira magia.`,
    turkish: `Bir sabah, pencerenin dışında şarkı söyleyen renkli bir kuş keşfettim. Hızla arkadaş olduk, günlük melodileri paylaştık. Uyum
            sağlamayı dileyerek farklı seslerimizi tanıyarak, esintiye bir dilek ile biraz sihir umuyordum. Sürprizime göre, ertesi gün
            mükemmel bir uyum içinde şarkı söylüyorduk, şarkılarımız herkesi memnun ediyordu. Bu deneyim bana paylaşmanın gücünü ve birliğin
            güzelliğini öğretti. Biraz sihir ve kalbin farklı sesleri güzelliğe birleştirebileceğini öğrenerek, uyumlu şarkılarımızı
            sürdürdük, neşe yaydık ve dostluk ve uyumun gerçek sihri temsil ettiğini vurguladık.`,
    czech: `Jednoho rána jsem objevil pestrobarevného ptáka, který zpíval za mým oknem. Rychle jsme se stali přáteli a denně sdíleli
            melodie. Přál jsem si harmonizovat, ale uvědomoval jsem si naše odlišné hlasy, tak jsem doufal v trochu kouzla s přáním do
            vánku. K mému překvapení jsme už následující den zpívali v dokonalé harmonii, naše písně potěšily všechny. Tato zkušenost mě
            naučila o síle sdílení a kráse jednoty. Učení se, že trochu kouzla a srdce může spojit různé hlasy do krásy, pokračovali jsme v
            našich harmonických písních, šířili radost a zdůraznili, že přátelství a harmonie jsou pravou magií.`,
    croatian: `Jednog jutra otkrio sam šarenog ptica kako pjeva izvan mog prozora. Brzo smo postali dobri prijatelji, svakodnevno dijeleći
            melodije. Želeći uskladiti, ali prepoznajući naše različite glasove, nadao sam se malo magije s željom u povjetarcu. Na moje
            iznenađenje, već sljedećeg dana pjevali smo u savršenoj harmoniji, naše pjesme oduševljavale su sve. Ovo iskustvo naučilo me o
            snazi dijeljenja i ljepoti jedinstva. Učeći da malo magije i srca može spojiti različite glasove u ljepotu, nastavili smo s
            našim harmoničnim pjesmama, šireći radost i naglašavajući da prijateljstvo i harmonija utjelovljuju pravu magiju.`,
    indonesian: `Suatu pagi, saya menemukan seekor burung berwarna-warni yang bernyanyi di luar jendela saya. Kami menjadi teman baik dengan
            cepat, berbagi melodi setiap hari. Berharap untuk berharmoni tetapi mengakui perbedaan suara kami, saya berharap sedikit sihir
            dengan sebuah keinginan ke angin. Untuk kejutan saya, pada hari berikutnya, kami bernyanyi dalam harmoni yang sempurna,
            lagu-lagu kami menggembirakan semua orang. Pengalaman ini mengajarkan saya tentang kekuatan berbagi dan keindahan kesatuan.
            Belajar bahwa sedikit sihir dan hati dapat menyatukan suara yang berbeda menjadi keindahan, kami melanjutkan lagu-lagu harmonis
            kami, menyebarkan kegembiraan dan menegaskan bahwa persahabatan dan harmoni merupakan sihir yang sebenarnya.`,
    polish: `Pewnego poranka odkryłem kolorowego ptaka śpiewającego za moim oknem. Szybko zostaliśmy dobrymi przyjaciółmi, codziennie dzieląc
            się melodiami. Chcąc harmonizować, ale zdając sobie sprawę z naszych różnych głosów, życzyłem sobie odrobiny magii z życzeniem w
            tchnieniu wiatru. Ku mojemu zdziwieniu, następnego dnia śpiewaliśmy w idealnej harmonii, nasze piosenki zachwycały wszystkich.
            To doświadczenie nauczyło mnie o sile dzielenia się i pięknie jedności. Ucząc się, że trochę magii i serca może połączyć różne
            głosy w piękno, kontynuowaliśmy nasze harmonijne piosenki, rozsiewając radość i podkreślając, że przyjaźń i harmonia uosabiają
            prawdziwą magię.`,
    romanian: `Într-o dimineață, am descoperit o pasăre colorată cântând în afara ferestrei mele. Am devenit repede prieteni, împărtășind
            melodii zilnic. Dorind să armonizăm, dar recunoscând vocile noastre diferite, am sperat la puțină magie cu o dorință în adierea
            vântului. Spre surprinderea mea, a doua zi cântam în perfectă armonie, cântecele noastre încântând pe toți. Această experiență
            m-a învățat despre puterea de a împărtăși și frumusețea unității. Învățând că puțină magie și inimă pot uni voci diferite în
            frumusețe, am continuat cântecele noastre armonioase, răspândind bucurie și subliniind că prietenia și armonia întruchipează
            adevărata magie.`,
    danish: `En morgen opdagede jeg en farverig fugl, der sang uden for mit vindue. Vi blev hurtigt gode venner og delte melodier dagligt.
            Ønsket om at harmonisere, men anerkendte vores forskellige stemmer, håbede jeg på lidt magi med et ønske i brisen. Til min
            overraskelse sang vi næste dag i perfekt harmoni, vores sange glædede alle. Denne oplevelse lærte mig om delingens kraft og
            enhedens skønhed. Ved at lære, at lidt magi og hjerte kan forene forskellige stemmer til skønhed, fortsatte vi vores harmoniske
            sange, spredte glæde og understregede, at venskab og harmoni udgør ægte magi.`,
    slovak: `Jedného rána som objavil farebného vtáka, ktorý spieval vonku pri mojom okne. Rýchlo sme sa stali priateľmi, každodenne si
            vymieňajúc melódie. Túžiac po harmonizácii, ale uznávajúc naše odlišné hlasy, dúfal som v trochu mágie s prianím do vetra. Na
            moje prekvapenie sme už na druhý deň spievali v dokonalej harmónii, naše piesne potešili všetkých. Táto skúsenosť ma naučila o
            sile zdieľania a kráse jednoty. Učiac sa, že trochu mágie a srdca môže zlúčiť rozdielne hlasy do krásy, pokračovali sme v našich
            harmónických piesňach, šírili radosť a zdôrazňovali, že priateľstvo a harmónia sú skutočnou mágiou.`,
    malay: `Pada suatu pagi, saya menemukan seekor burung berwarna-warni yang bernyanyi di luar jendela saya. Kami menjadi teman baik dengan
            cepat, berbagi melodi setiap hari. Berharap untuk berharmoni tetapi mengakui perbedaan suara kami, saya berharap sedikit sihir
            dengan sebuah keinginan ke angin. Untuk kejutan saya, pada hari berikutnya, kami bernyanyi dalam harmoni yang sempurna,
            lagu-lagu kami menggembirakan semua orang. Pengalaman ini mengajarkan saya tentang kekuatan berbagi dan keindahan kesatuan.
            Belajar bahwa sedikit sihir dan hati dapat menyatukan suara yang berbeda menjadi keindahan, kami melanjutkan lagu-lagu harmonis
            kami, menyebarkan kegembiraan dan menegaskan bahwa persahabatan dan harmoni merupakan sihir yang sebenarnya.`,
};

const expressAvatarTextByLanguage: Record<SupportedLanguage, string> = {
    english: `<Look directly into the camera with a natural friendly expression. During pauses, make sure your lips are closed.>

Hello! Today, I’ll be reading a short script to help create a digital avatar that accurately represents me. 

This avatar will serve as a digital twin, capturing my unique movements, expressions and voice. 

I will now read a brief script to train my avatar. It can be as short as ${AvatarTypeToRecordingTimes.express.minRecordingTime} seconds, but it's recommended to record at least two minutes. 

Please join me as we create my avatar!

Throughout this recording, I’ll maintain a steady pace and clear articulation. 

It’s essential that I remain natural, relaxed, and consistent while reading the script to achieve the best possible result.

 Doing this ensures that the avatar created will be as realistic as possible. 

So, while recording I am mindful of my movements, using natural gestures, making sure my hands remain relaxed and do not cover my face. 

I feel great reading through this script. I bring more expression to my eyes and a genuine smile to my face.

I am shooting the best version of myself, which will create an even better avatar. 

<Pause and keep your lips closed>

Let me share a few fascinating insights with you.

Did you know that meditation has been shown to actually increase the size of your brain’s gray matter? 

So, when you take a moment to be still, you’re not just calming your mind—you’re literally growing your brain!

On a different note, laughter is incredibly beneficial—not just for the soul but also for the body. It can lower stress and even boost your immune system.

So, next time you laugh, know that you’re not just having fun; you’re also taking care of your health.

Now, I’ll take a moment to look directly into the camera and pause briefly.

<Pause and keep your lips closed>

As I continue to read this script, I'm feeling excited. 

I can't wait to see my avatar come to life. 

I'm sharing my thoughts, while my voice remains calm and my tone remains inviting.

Isn’t it fascinating how technology allows us to create digital clones? 

My avatar can be used in a variety of ways, from enhancing communication to providing personalized experiences. 

The avatar that will be created from this footage will be a powerful tool for various applications. 

Whether it’s for professional use, entertainment, or personal interactions, this digital version of myself will be a valuable asset. 

The ability to extend my presence into a digital realm opens up countless possibilities for communication and engagement.

<Pause and keep your lips closed>

I’m excited to see how this technology brings my digital avatar to life. 

By following these steps, I’ll be able to create something innovative and truly reflective of who I am.
 
Get ready to experience something truly exceptional!

Imagine the transformative impact we can achieve with this groundbreaking idea!

We’re at the forefront of change, and the future holds limitless possibilities!

This isn’t just progress, it’s a revolutionary step forward!

Let’s make a great avatar! 

<Pause and keep your lips closed>`,
    spanish: `<Mira directamente a la cámara con una expresión amigable y natural. Durante las pausas, asegúrate de que tus labios estén cerrados.>

¡Hola! Hoy leeré un guion breve para ayudar a crear un avatar digital que me represente con precisión.

Este avatar servirá como mi gemelo digital, capturando mis movimientos, expresiones y mi voz única.

Ahora leeré un guion breve para entrenar a mi avatar. Puede ser tan corto como un minuto, pero se recomienda grabar al menos dos minutos.

¡Únete a mí mientras creamos mi avatar!

Durante esta grabación, mantendré un ritmo constante y una articulación clara.

Es esencial que permanezca natural, relajado y constante mientras leo el guion para lograr el mejor resultado posible.

Hacer esto garantiza que el avatar que se cree será lo más realista posible.

Así que, mientras grabo, soy consciente de mis movimientos, uso gestos naturales y me aseguro de que mis manos permanezcan relajadas y no cubran mi rostro.

Me siento genial leyendo este guion. Le doy más expresión a mis ojos y una sonrisa genuina a mi rostro.

Estoy grabando la mejor versión de mí mismo, lo que creará un avatar aún mejor.

<Pausa y mantén los labios cerrados>

Permíteme compartir contigo algunos datos fascinantes.

¿Sabías que la meditación puede aumentar el tamaño de la materia gris de tu cerebro?

Así que, cuando tomas un momento para estar en calma, no solo estás calmando tu mente, ¡estás literalmente haciendo crecer tu cerebro!

Por otro lado, reír es increíblemente beneficioso, no solo para el alma, sino también para el cuerpo. Puede reducir el estrés e incluso fortalecer tu sistema inmunológico.

Así que, la próxima vez que rías, recuerda que no solo te estás divirtiendo; también estás cuidando tu salud.

Ahora, tomaré un momento para mirar directamente a la cámara y hacer una breve pausa.

<Pausa y mantén los labios cerrados>

A medida que continúo leyendo este guion, me siento emocionado.

No puedo esperar a ver a mi avatar cobrar vida.

Estoy compartiendo mis pensamientos, mientras mi voz permanece calmada y mi tono es acogedor.

¿No es fascinante cómo la tecnología nos permite crear clones digitales?

Mi avatar se puede usar de muchas maneras, desde mejorar la comunicación hasta brindar experiencias personalizadas.

El avatar que se creará a partir de esta grabación será una herramienta poderosa para diversas aplicaciones.

Ya sea para uso profesional, entretenimiento o interacciones personales, esta versión digital de mí será un activo valioso.

La capacidad de extender mi presencia a un mundo digital abre innumerables posibilidades de comunicación e interacción.

<Pausa y mantén los labios cerrados>

Estoy emocionado de ver cómo esta tecnología da vida a mi avatar digital.

Siguiendo estos pasos, seré capaz de crear algo innovador y que realmente refleje quién soy.

¡Prepárate para experimentar algo verdaderamente excepcional!

¡Imagina el impacto transformador que podemos lograr con esta idea revolucionaria!

¡Estamos a la vanguardia del cambio, y el futuro ofrece posibilidades ilimitadas!

¡Esto no es solo progreso, es un paso revolucionario hacia adelante!

¡Hagamos un gran avatar!

<Pausa y mantén los labios cerrados>`,
    italian: `<Guarda direttamente la telecamera con un'espressione naturale e amichevole. Durante le pause, assicurati che le labbra siano chiuse.>

Ciao! Oggi leggerò un breve copione per aiutare a creare un avatar digitale che mi rappresenti accuratamente.

Questo avatar fungerà da gemello digitale, catturando i miei movimenti, espressioni e la mia voce unici.

Ora leggerò un breve copione per allenare il mio avatar. Può durare anche solo un minuto, ma si consiglia di registrare almeno due minuti.

Unisciti a me mentre creiamo il mio avatar!

Durante questa registrazione, manterrò un ritmo costante e una chiara articolazione.

È essenziale che io rimanga naturale, rilassato e coerente mentre leggo il copione per ottenere il miglior risultato possibile.

Facendo questo, mi assicuro che l'avatar creato sarà il più realistico possibile.

Quindi, mentre registro, sono consapevole dei miei movimenti, uso gesti naturali e mi assicuro che le mani rimangano rilassate e non coprano il mio viso.

Mi sento benissimo mentre leggo questo copione. Porto più espressione ai miei occhi e un sorriso sincero sul mio viso.

Sto registrando la migliore versione di me stesso, che creerà un avatar ancora migliore.

<Pausa e mantieni le labbra chiuse>
Permettimi di condividere con te alcune affascinanti curiosità.

Sapevi che la meditazione può effettivamente aumentare la dimensione della materia grigia nel cervello?

Quindi, quando ti prendi un momento per stare fermo, non stai solo calmando la tua mente — stai letteralmente accrescendo il tuo cervello!

D'altra parte, il ridere è incredibilmente benefico — non solo per l'anima, ma anche per il corpo. Può ridurre lo stress e persino rafforzare il sistema immunitario.

Quindi, la prossima volta che ridi, sappi che non ti stai solo divertendo; ti stai anche prendendo cura della tua salute.

Ora prenderò un momento per guardare direttamente la telecamera e fare una breve pausa.

<Pausa e mantieni le labbra chiuse>
Mentre continuo a leggere questo copione, mi sento entusiasta.

Non vedo l'ora di vedere il mio avatar prendere vita.

Sto condividendo i miei pensieri, mentre la mia voce rimane calma e il mio tono rimane invitante.

Non è affascinante come la tecnologia ci permetta di creare cloni digitali?

Il mio avatar può essere utilizzato in vari modi, dal miglioramento della comunicazione alla fornitura di esperienze personalizzate.

L'avatar che verrà creato da questa registrazione sarà uno strumento potente per varie applicazioni.

Che sia per uso professionale, intrattenimento o interazioni personali, questa versione digitale di me sarà una risorsa preziosa.

La capacità di estendere la mia presenza in un mondo digitale apre innumerevoli possibilità di comunicazione e coinvolgimento.

<Pausa e mantieni le labbra chiuse>
Sono entusiasta di vedere come questa tecnologia darà vita al mio avatar digitale.

Seguendo questi passaggi, sarò in grado di creare qualcosa di innovativo e veramente riflessivo di chi sono.

Preparati a vivere qualcosa di veramente eccezionale!

Immagina l'impatto trasformativo che possiamo ottenere con questa idea rivoluzionaria!

Siamo in prima linea nel cambiamento, e il futuro riserva possibilità illimitate!

Questo non è solo progresso, è un passo rivoluzionario avanti!

Creiamo un avatar fantastico!

<Pausa e mantieni le labbra chiuse>`,
    swedish: `<Titta direkt in i kameran med ett naturligt och vänligt uttryck. Se till att dina läppar är stängda under pauser.>

Hej! Idag kommer jag att läsa ett kort manuskript för att hjälpa till att skapa en digital avatar som exakt representerar mig.

Denna avatar kommer att fungera som en digital tvilling och fånga mina unika rörelser, uttryck och min röst.

Nu kommer jag att läsa ett kort manuskript för att träna min avatar. Det kan vara så kort som en minut, men det rekommenderas att spela in i minst två minuter.

Följ med mig medan vi skapar min avatar!

Under denna inspelning kommer jag att hålla ett jämnt tempo och tydlig artikulation.

Det är viktigt att jag förblir naturlig, avslappnad och konsekvent medan jag läser manuskriptet för att uppnå bästa möjliga resultat.

Att göra detta säkerställer att avataren som skapas blir så realistisk som möjligt.

Så, medan jag spelar in, är jag medveten om mina rörelser, använder naturliga gester och ser till att mina händer förblir avslappnade och inte täcker mitt ansikte.

Jag känner mig bra när jag läser detta manuskript. Jag ger mer uttryck i mina ögon och ett genuint leende på mitt ansikte.

Jag spelar in den bästa versionen av mig själv, vilket kommer att skapa en ännu bättre avatar.

<Pausa och håll läpparna stängda>

Låt mig dela några fascinerande insikter med dig.

Visste du att meditation faktiskt kan öka mängden grå substans i din hjärna?

Så, när du tar dig en stund att vara stilla, lugnar du inte bara ditt sinne – du bokstavligen växer din hjärna!

Å andra sidan är skratt otroligt fördelaktigt – inte bara för själen utan också för kroppen. Det kan minska stress och till och med stärka ditt immunförsvar.

Så nästa gång du skrattar, vet att du inte bara har roligt; du tar också hand om din hälsa.

Nu ska jag ta en stund och titta rakt in i kameran och pausa kort.

<Pausa och håll läpparna stängda>

När jag fortsätter att läsa detta manuskript känner jag mig upprymd.

Jag kan knappt vänta på att se min avatar komma till liv.

Jag delar mina tankar medan min röst förblir lugn och min ton inbjudande.

Är det inte fascinerande hur teknologin låter oss skapa digitala kloner?

Min avatar kan användas på olika sätt, från att förbättra kommunikation till att erbjuda personliga upplevelser.

Avataren som kommer att skapas från denna inspelning kommer att vara ett kraftfullt verktyg för olika tillämpningar.

Oavsett om det är för professionellt bruk, underhållning eller personliga interaktioner, kommer denna digitala version av mig själv att vara en värdefull tillgång.

Förmågan att utöka min närvaro i en digital värld öppnar otaliga möjligheter för kommunikation och engagemang.

<Pausa och håll läpparna stängda>

Jag är exalterad över att se hur denna teknik kommer att ge liv åt min digitala avatar.

Genom att följa dessa steg kommer jag att kunna skapa något innovativt och verkligen reflekterande över vem jag är.

Förbered dig på att uppleva något riktigt exceptionellt!

Föreställ dig den transformativa påverkan vi kan uppnå med denna banbrytande idé!

Vi är i framkant av förändringen och framtiden erbjuder obegränsade möjligheter!

Detta är inte bara framsteg, det är ett revolutionerande steg framåt!

Låt oss skapa en fantastisk avatar!

<Pausa och håll läpparna stängda>`,
    german: `<Schauen Sie direkt in die Kamera mit einem natürlichen, freundlichen Ausdruck. Achten Sie darauf, dass Ihre Lippen während der Pausen geschlossen sind.>

Hallo! Heute werde ich ein kurzes Skript vorlesen, um dabei zu helfen, einen digitalen Avatar zu erstellen, der mich genau repräsentiert.

Dieser Avatar wird als digitaler Zwilling dienen, der meine einzigartigen Bewegungen, Ausdrücke und meine Stimme einfängt.

Ich werde nun ein kurzes Skript vorlesen, um meinen Avatar zu trainieren. Es kann so kurz wie eine Minute sein, aber es wird empfohlen, mindestens zwei Minuten aufzuzeichnen.

Begleiten Sie mich, während wir meinen Avatar erschaffen!

Während dieser Aufnahme werde ich ein gleichmäßiges Tempo und eine klare Aussprache beibehalten.

Es ist wichtig, dass ich natürlich, entspannt und konsistent bleibe, während ich das Skript vorlese, um das bestmögliche Ergebnis zu erzielen.

Auf diese Weise wird sichergestellt, dass der erstellte Avatar so realistisch wie möglich ist.

Also, während ich aufnehme, achte ich auf meine Bewegungen, benutze natürliche Gesten und stelle sicher, dass meine Hände entspannt bleiben und nicht mein Gesicht bedecken.

Ich fühle mich gut, während ich dieses Skript lese. Ich bringe mehr Ausdruck in meine Augen und ein echtes Lächeln auf mein Gesicht.

Ich filme die beste Version von mir selbst, was einen noch besseren Avatar erzeugen wird.

<Pausieren und halten Sie Ihre Lippen geschlossen>
Lassen Sie mich ein paar faszinierende Einblicke mit Ihnen teilen.

Wussten Sie, dass Meditation tatsächlich die Größe der grauen Substanz in Ihrem Gehirn erhöhen kann?

Wenn Sie also einen Moment der Stille genießen, beruhigen Sie nicht nur Ihren Geist – Sie erweitern buchstäblich Ihr Gehirn!

Auf einer anderen Ebene ist Lachen unglaublich wohltuend – nicht nur für die Seele, sondern auch für den Körper. Es kann Stress reduzieren und sogar Ihr Immunsystem stärken.

Das nächste Mal, wenn Sie lachen, wissen Sie also, dass Sie nicht nur Spaß haben; Sie kümmern sich auch um Ihre Gesundheit.

Jetzt nehme ich mir einen Moment Zeit, um direkt in die Kamera zu schauen und kurz zu pausieren.

<Pausieren und halten Sie Ihre Lippen geschlossen>
Während ich dieses Skript weiterlese, fühle ich mich begeistert.

Ich kann es kaum erwarten, meinen Avatar zum Leben erwecken zu sehen.

Ich teile meine Gedanken, während meine Stimme ruhig bleibt und mein Ton einladend ist.

Ist es nicht faszinierend, wie die Technologie es uns ermöglicht, digitale Klone zu erschaffen?

Mein Avatar kann auf verschiedene Weise verwendet werden, von der Verbesserung der Kommunikation bis hin zur Bereitstellung personalisierter Erlebnisse.

Der Avatar, der aus dieser Aufnahme erstellt wird, wird ein mächtiges Werkzeug für verschiedene Anwendungen sein.

Ob für berufliche Zwecke, Unterhaltung oder persönliche Interaktionen, diese digitale Version von mir wird ein wertvolles Gut sein.

Die Fähigkeit, meine Präsenz in einer digitalen Welt zu erweitern, eröffnet unzählige Möglichkeiten für Kommunikation und Interaktion.

<Pausieren und halten Sie Ihre Lippen geschlossen>
Ich bin gespannt darauf, zu sehen, wie diese Technologie meinen digitalen Avatar zum Leben erweckt.

Indem ich diesen Schritten folge, werde ich in der Lage sein, etwas Innovatives zu schaffen, das wirklich widerspiegelt, wer ich bin.

Machen Sie sich bereit, etwas wirklich Außergewöhnliches zu erleben!

Stellen Sie sich vor, welchen transformativen Einfluss wir mit dieser bahnbrechenden Idee erreichen können!

Wir stehen an der Spitze des Wandels, und die Zukunft hält unbegrenzte Möglichkeiten bereit!

Das ist nicht nur Fortschritt, es ist ein revolutionärer Schritt nach vorne!

Lassen Sie uns einen großartigen Avatar erschaffen!

<Pausieren und halten Sie Ihre Lippen geschlossen>`,
    french: `<Regardez directement la caméra avec une expression amicale et naturelle. Pendant les pauses, assurez-vous que vos lèvres sont fermées.>

Bonjour ! Aujourd'hui, je vais lire un court script pour aider à créer un avatar numérique qui me représente fidèlement.

Cet avatar servira de double numérique, capturant mes mouvements, expressions et ma voix uniques.

Je vais maintenant lire un bref script pour entraîner mon avatar. Cela peut durer aussi peu qu'une minute, mais il est recommandé d'enregistrer au moins deux minutes.

Rejoignez-moi dans la création de mon avatar !

Tout au long de cet enregistrement, je vais maintenir un rythme régulier et une articulation claire.

Il est essentiel que je reste naturel, détendu et constant pendant que je lis le script pour obtenir le meilleur résultat possible.

Cela garantit que l'avatar créé sera aussi réaliste que possible.

Ainsi, pendant l'enregistrement, je fais attention à mes mouvements, j'utilise des gestes naturels et je m'assure que mes mains restent détendues et ne couvrent pas mon visage.

Je me sens bien en lisant ce script. J'apporte plus d'expression à mes yeux et un sourire sincère sur mon visage.

Je suis en train de filmer la meilleure version de moi-même, ce qui créera un avatar encore meilleur.

<Pause et gardez vos lèvres fermées>

Permettez-moi de partager quelques faits fascinants avec vous.

Saviez-vous que la méditation peut en réalité augmenter la taille de la matière grise de votre cerveau ?

Ainsi, lorsque vous prenez un moment pour être calme, vous ne faites pas que calmer votre esprit — vous développez littéralement votre cerveau !

Sur une autre note, le rire est incroyablement bénéfique — non seulement pour l'âme, mais aussi pour le corps. Il peut réduire le stress et même renforcer votre système immunitaire.

Alors, la prochaine fois que vous riez, sachez que vous ne faites pas que vous amuser ; vous prenez également soin de votre santé.

Maintenant, je vais prendre un moment pour regarder directement la caméra et faire une courte pause.

<Pause et gardez vos lèvres fermées>

En continuant à lire ce script, je me sens enthousiaste.

J'ai hâte de voir mon avatar prendre vie.

Je partage mes pensées, tandis que ma voix reste calme et mon ton accueillant.

N'est-il pas fascinant de voir comment la technologie nous permet de créer des clones numériques ?

Mon avatar peut être utilisé de nombreuses façons, de l'amélioration de la communication à la fourniture d'expériences personnalisées.

L'avatar qui sera créé à partir de cet enregistrement sera un outil puissant pour diverses applications.

Que ce soit pour un usage professionnel, du divertissement ou des interactions personnelles, cette version numérique de moi-même sera un atout précieux.

La capacité d'étendre ma présence dans un monde numérique ouvre d'innombrables possibilités de communication et d'engagement.

<Pause et gardez vos lèvres fermées>

Je suis impatient de voir comment cette technologie va donner vie à mon avatar numérique.

En suivant ces étapes, je vais pouvoir créer quelque chose d'innovant et qui reflète véritablement qui je suis.

Préparez-vous à vivre quelque chose de vraiment exceptionnel !

Imaginez l'impact transformateur que nous pouvons accomplir avec cette idée révolutionnaire !

Nous sommes à l'avant-garde du changement, et l'avenir réserve des possibilités illimitées !

Ce n'est pas juste du progrès, c'est une avancée révolutionnaire !

Créons un super avatar !

<Pause et gardez vos lèvres fermées>`,
    dutch: `<Kijk direct in de camera met een natuurlijke, vriendelijke gezichtsuitdrukking. Zorg ervoor dat je je lippen gesloten houdt tijdens de pauzes.>

Hallo! Vandaag lees ik een kort script om te helpen bij het creëren van een digitale avatar die mij nauwkeurig vertegenwoordigt.

Deze avatar zal dienen als een digitale tweeling, die mijn unieke bewegingen, uitdrukkingen en stem vastlegt.

Ik zal nu een kort script lezen om mijn avatar te trainen. Het kan zo kort zijn als één minuut, maar het is aan te raden om minstens twee minuten op te nemen.

Doe met me mee terwijl we mijn avatar maken!

Tijdens deze opname houd ik een gelijkmatig tempo aan en articuleer ik duidelijk.

Het is essentieel dat ik natuurlijk, ontspannen en consistent blijf terwijl ik het script lees om het best mogelijke resultaat te bereiken.

Dit zorgt ervoor dat de avatar die gecreëerd wordt zo realistisch mogelijk is.

Dus, tijdens het opnemen let ik op mijn bewegingen, gebruik ik natuurlijke gebaren en zorg ik ervoor dat mijn handen ontspannen blijven en mijn gezicht niet bedekken.

Ik voel me goed terwijl ik dit script lees. Ik geef meer expressie aan mijn ogen en heb een oprechte glimlach op mijn gezicht.

Ik maak de beste versie van mezelf, wat een nog betere avatar zal opleveren.

<Pauzeer en houd je lippen gesloten>
Laat me enkele fascinerende inzichten met je delen.

Wist je dat meditatie de hoeveelheid grijze massa in je hersenen daadwerkelijk kan vergroten?

Dus, wanneer je een moment neemt om stil te zijn, kalmeer je niet alleen je geest – je vergroot letterlijk je hersenen!

Aan de andere kant is lachen ontzettend voordelig – niet alleen voor de ziel, maar ook voor het lichaam. Het kan stress verminderen en zelfs je immuunsysteem versterken.

Dus de volgende keer dat je lacht, weet dan dat je niet alleen plezier hebt; je zorgt ook voor je gezondheid.

Nu neem ik even de tijd om direct in de camera te kijken en kort te pauzeren.

<Pauzeer en houd je lippen gesloten>
Terwijl ik verder lees in dit script, voel ik me enthousiast.

Ik kan niet wachten om mijn avatar tot leven te zien komen.

Ik deel mijn gedachten, terwijl mijn stem kalm blijft en mijn toon uitnodigend is.

Is het niet fascinerend hoe technologie ons in staat stelt om digitale klonen te creëren?

Mijn avatar kan op verschillende manieren worden gebruikt, van het verbeteren van communicatie tot het bieden van gepersonaliseerde ervaringen.

De avatar die uit deze opname wordt gecreëerd, zal een krachtig hulpmiddel zijn voor diverse toepassingen.

Of het nu voor professioneel gebruik is, entertainment, of persoonlijke interacties, deze digitale versie van mezelf zal een waardevolle aanwinst zijn.

Het vermogen om mijn aanwezigheid uit te breiden naar een digitale wereld opent talloze mogelijkheden voor communicatie en interactie.

<Pauzeer en houd je lippen gesloten>
Ik ben enthousiast om te zien hoe deze technologie mijn digitale avatar tot leven brengt.

Door deze stappen te volgen, zal ik in staat zijn om iets innovatiefs te creëren dat echt weerspiegelt wie ik ben.

Maak je klaar om iets echt uitzonderlijks te ervaren!

Stel je voor welke transformatieve impact we kunnen bereiken met dit baanbrekende idee!

We staan aan de vooravond van verandering, en de toekomst biedt onbeperkte mogelijkheden!

Dit is niet zomaar vooruitgang, het is een revolutionaire stap vooruit!

Laten we een geweldige avatar maken!

<Pauzeer en houd je lippen gesloten>`,
    filipino: `<Tumingin nang diretso sa camera na may natural na palakaibigang ekspresyon. Siguraduhing sarado ang iyong mga labi sa mga pahinga.>

Kamusta! Ngayon ay magbabasa ako ng maikling script upang makatulong sa paglikha ng isang digital na avatar na tumpak na kumakatawan sa akin.

Ang avatar na ito ay magsisilbing aking digital na kambal, na kinukuha ang aking mga natatanging galaw, ekspresyon, at boses.

Ngayon, magbabasa ako ng isang maikling script para sanayin ang aking avatar. Maaari itong maging kasing-ikli ng isang minuto, ngunit inirerekomenda na mag-record ng hindi bababa sa dalawang minuto.

Samahan mo ako sa paglikha ng aking avatar!

Sa buong pag-record na ito, pananatilihin ko ang isang steady na bilis at malinaw na pagbigkas.

Napakahalaga na manatili akong natural, relaxed, at consistent habang binabasa ang script upang makamit ang pinakamahusay na posibleng resulta.

Ang paggawa nito ay nagsisiguro na ang avatar na nilikha ay magiging kasing realistiko hangga't maaari.

Kaya, habang nagre-record, ako ay mindful sa aking mga galaw, gumagamit ng natural na gestures, at tinitiyak na ang aking mga kamay ay relaxed at hindi natatakpan ang aking mukha.

Masaya akong binabasa ang script na ito. Nagdadala ako ng higit pang ekspresyon sa aking mga mata at tunay na ngiti sa aking mukha.

I’m shooting the best version of myself, na magdudulot ng isang mas mahusay na avatar.

<Pahinga at sarado ang iyong mga labi>
Hayaan mong ibahagi ko sa iyo ang ilang kamangha-manghang impormasyon.

Alam mo ba na ang pagmumuni-muni ay napatunayang maaaring magpalaki ng gray matter ng iyong utak?

Kaya’t kapag kumuha ka ng sandali upang manahimik, hindi mo lang pinapakalma ang iyong isipan—literal na pinapalaki mo ang iyong utak!

Sa kabilang banda, ang pagtawa ay napaka-benepisyaryo—hindi lang para sa kaluluwa kundi pati na rin sa katawan. Maaari itong magpababa ng stress at kahit na magpalakas ng iyong immune system.

Kaya, sa susunod na tumawa ka, alamin na hindi ka lang nagsasaya; inaalagaan mo rin ang iyong kalusugan.

Ngayon, maglalaan ako ng sandali upang tumingin nang direkta sa camera at sandaling tumigil.

<Pahinga at sarado ang iyong mga labi>
Habang ipinagpapatuloy ko ang pagbasa ng script na ito, nararamdaman ko ang kasiyahan.

Hindi ako makapaghintay na makita ang aking avatar na nabubuhay.

Ibinabahagi ko ang aking mga iniisip, habang nananatiling kalmado ang aking boses at imbitasyon ang tono.

Hindi ba kamangha-mangha kung paano pinapayagan tayo ng teknolohiya na lumikha ng mga digital na kopya?

Ang aking avatar ay maaaring gamitin sa iba't ibang paraan, mula sa pagpapabuti ng komunikasyon hanggang sa pagbibigay ng personalized na mga karanasan.

Ang avatar na gagawin mula sa recording na ito ay magiging isang makapangyarihang tool para sa iba't ibang mga aplikasyon.

Maging para sa propesyonal na paggamit, entertainment, o personal na pakikipag-ugnayan, ang digital na bersyon ng aking sarili ay magiging isang mahalagang asset.

Ang kakayahang palawakin ang aking presensya sa isang digital na mundo ay nagbubukas ng walang katapusang mga posibilidad para sa komunikasyon at pakikipag-ugnayan.

<Pahinga at sarado ang iyong mga labi>
Excited akong makita kung paano binubuhay ng teknolohiyang ito ang aking digital na avatar.

Sa pamamagitan ng pagsunod sa mga hakbang na ito, magagawa kong lumikha ng isang bagay na makabago at tunay na sumasalamin kung sino ako.

Maghanda upang maranasan ang isang tunay na kakaibang bagay!

Isipin ang transformative impact na maaari nating makamit sa groundbreaking na ideyang ito!

Nasa unahan tayo ng pagbabago, at ang hinaharap ay puno ng walang katapusang mga posibilidad!

Hindi lang ito progreso, ito ay isang rebolusyonaryong hakbang pasulong!

Gumawa tayo ng isang mahusay na avatar!

<Pahinga at sarado ang iyong mga labi>`,
    portuguese: `<Olhe diretamente para a câmera com uma expressão natural e amigável. Durante as pausas, certifique-se de que seus lábios estão fechados.>

Olá! Hoje, vou ler um pequeno roteiro para ajudar a criar um avatar digital que me represente com precisão.

Este avatar servirá como um gêmeo digital, capturando meus movimentos, expressões e voz únicos.

Agora vou ler um breve roteiro para treinar meu avatar. Pode durar apenas um minuto, mas recomenda-se gravar pelo menos dois minutos.

Junte-se a mim enquanto criamos meu avatar!

Durante esta gravação, manterei um ritmo constante e uma articulação clara.

É essencial que eu permaneça natural, relaxado e consistente ao ler o roteiro para obter o melhor resultado possível.

Fazer isso garante que o avatar criado será o mais realista possível.

Então, durante a gravação, estou atento aos meus movimentos, usando gestos naturais e garantindo que minhas mãos permaneçam relaxadas e não cubram meu rosto.

Eu me sinto ótimo lendo este roteiro. Eu trago mais expressão para os meus olhos e um sorriso genuíno para o meu rosto.

Estou filmando a melhor versão de mim mesmo, o que criará um avatar ainda melhor.

<Pare e mantenha seus lábios fechados>

Deixe-me compartilhar alguns fatos fascinantes com você.

Você sabia que a meditação pode aumentar o tamanho da matéria cinzenta do seu cérebro?

Então, quando você tira um momento para ficar quieto, você não está apenas acalmando sua mente — você está literalmente fazendo seu cérebro crescer!

Por outro lado, o riso é incrivelmente benéfico — não apenas para a alma, mas também para o corpo. Ele pode reduzir o estresse e até fortalecer seu sistema imunológico.

Então, da próxima vez que você rir, saiba que você não está apenas se divertindo; você também está cuidando da sua saúde.

Agora, vou dedicar um momento para olhar diretamente para a câmera e fazer uma breve pausa.

<Pare e mantenha seus lábios fechados>

À medida que continuo a ler este roteiro, sinto-me animado.

Estou ansioso para ver meu avatar ganhar vida.

Estou compartilhando meus pensamentos, enquanto minha voz permanece calma e meu tom convidativo.

Não é fascinante como a tecnologia nos permite criar clones digitais?

Meu avatar pode ser usado de várias maneiras, desde melhorar a comunicação até fornecer experiências personalizadas.

O avatar que será criado a partir desta gravação será uma ferramenta poderosa para várias aplicações.

Seja para uso profissional, entretenimento ou interações pessoais, essa versão digital de mim será um ativo valioso.

A capacidade de estender minha presença ao mundo digital abre inúmeras possibilidades de comunicação e engajamento.

<Pare e mantenha seus lábios fechados>

Estou animado para ver como esta tecnologia trará meu avatar digital à vida.

Seguindo esses passos, serei capaz de criar algo inovador e verdadeiramente reflexivo de quem eu sou.

Prepare-se para experimentar algo verdadeiramente excepcional!

Imagine o impacto transformador que podemos alcançar com essa ideia revolucionária!

Estamos na vanguarda da mudança, e o futuro oferece possibilidades ilimitadas!

Isso não é apenas progresso, é um passo revolucionário para a frente!

Vamos criar um grande avatar!

<Pare e mantenha seus lábios fechados>`,
    turkish: `<Kameraya doğal ve dostane bir ifadeyle bakın. Duraklamalar sırasında dudaklarınızın kapalı olduğundan emin olun.>

Merhaba! Bugün beni doğru bir şekilde temsil edecek bir dijital avatar yaratılmasına yardımcı olmak için kısa bir metin okuyacağım.

Bu avatar, benzersiz hareketlerimi, ifadelerimi ve sesimi yakalayan bir dijital ikiz olarak hizmet edecek.

Şimdi avatarımı eğitmek için kısa bir metin okuyacağım. Bir dakika kadar kısa olabilir, ancak en az iki dakika kayıt yapmanız tavsiye edilir.

Avatarımı yaratırken bana katılın!

Bu kayıtta, sabit bir tempo ve net bir telaffuz sürdüreceğim.

En iyi sonucu elde etmek için metni okurken doğal, rahat ve tutarlı kalmam çok önemli.

Bunu yapmak, oluşturulan avatarın mümkün olduğunca gerçekçi olmasını sağlar.

Bu yüzden kayıt sırasında hareketlerime dikkat ediyorum, doğal jestler kullanıyorum ve ellerimin rahat olmasını ve yüzümü kapatmadığından emin oluyorum.

Bu metni okurken kendimi harika hissediyorum. Gözlerime daha fazla ifade katıyor ve yüzümde samimi bir gülümseme beliriyor.

Kendimin en iyi versiyonunu çekiyorum, bu da daha iyi bir avatar yaratacak.

<Duraklayın ve dudaklarınızı kapalı tutun>

Sizinle bazı ilginç bilgiler paylaşmak istiyorum.

Biliyor muydunuz ki meditasyon beyninizdeki gri madde miktarını artırabilir?

Yani, sessiz bir an geçirdiğinizde, sadece zihninizi sakinleştirmiyorsunuz — kelimenin tam anlamıyla beyninizi büyütüyorsunuz!

Öte yandan, gülmek inanılmaz derecede faydalıdır — sadece ruh için değil, aynı zamanda beden için de. Stresi azaltabilir ve hatta bağışıklık sisteminizi güçlendirebilir.

Yani, bir dahaki sefere güldüğünüzde, sadece eğlenmediğinizi bilin; aynı zamanda sağlığınıza da dikkat ediyorsunuz.

Şimdi kameraya doğrudan bakıp kısa bir süre duracağım.

<Duraklayın ve dudaklarınızı kapalı tutun>

Bu metni okumaya devam ederken heyecanlanıyorum.

Avatarımın hayata geçmesini görmek için sabırsızlanıyorum.

Düşüncelerimi paylaşıyorum, sesim sakin kalırken tonum davetkar olmaya devam ediyor.

Teknolojinin bize dijital klonlar yaratma imkanı vermesi ne kadar büyüleyici, değil mi?

Avatarım, iletişimi geliştirmekten kişiselleştirilmiş deneyimler sunmaya kadar birçok farklı şekilde kullanılabilir.

Bu kayıttan oluşturulacak avatar, çeşitli uygulamalar için güçlü bir araç olacaktır.

İster profesyonel kullanım, ister eğlence, isterse kişisel etkileşimler için olsun, bu dijital versiyonum değerli bir varlık olacak.

Varlığımı dijital bir dünyaya genişletebilme yeteneği, iletişim ve etkileşim için sayısız olasılığın kapılarını açıyor.

<Duraklayın ve dudaklarınızı kapalı tutun>

Bu teknolojinin dijital avatarımı nasıl hayata geçireceğini görmek için sabırsızlanıyorum.

Bu adımları izleyerek, kim olduğumu gerçekten yansıtan yenilikçi bir şey yaratabileceğim.

Gerçekten olağanüstü bir şey deneyimlemeye hazır olun!

Bu çığır açan fikirle nasıl dönüştürücü bir etki yaratabileceğimizi hayal edin!

Değişimin ön saflarındayız ve gelecek sınırsız olasılıklar sunuyor!

Bu sadece ilerleme değil, devrim niteliğinde bir adım!

Harika bir avatar yaratalım!

<Duraklayın ve dudaklarınızı kapalı tutun>`,
    czech: `<Dívejte se přímo do kamery s přirozeným přátelským výrazem. Během pauz se ujistěte, že máte zavřená ústa.>

Ahoj! Dnes přečtu krátký text, který pomůže vytvořit digitální avatar, který mě přesně představuje.

Tento avatar bude sloužit jako můj digitální dvojník, zachycující mé jedinečné pohyby, výrazy a hlas.

Nyní přečtu krátký text pro nácvik mého avatara. Může být krátký až jednu minutu, ale doporučuje se nahrávat alespoň dvě minuty.

Připojte se ke mně při vytváření mého avatara!

Během tohoto nahrávání budu udržovat stálé tempo a jasnou výslovnost.

Je nezbytné, abych zůstal přirozený, uvolněný a konzistentní při čtení textu, aby se dosáhlo co nejlepších výsledků.

Tím se zajistí, že vytvořený avatar bude co nejrealističtější.

Takže při nahrávání jsem si vědom svých pohybů, používám přirozené gesta a dbám na to, aby mé ruce zůstaly uvolněné a nezakrývaly můj obličej.

Cítím se skvěle při čtení tohoto textu. Dávám svým očím více výrazu a na tváři mám upřímný úsměv.

Natáčím nejlepší verzi sebe, což vytvoří ještě lepšího avatara.

<Pauza a zavřete ústa>

Rád bych se s vámi podělil o několik zajímavých postřehů.

Víte, že meditace může skutečně zvětšit objem šedé hmoty ve vašem mozku?

Když se na chvíli zastavíte, neuklidníte jen svou mysl – doslova zvětšujete svůj mozek!

A na jinou notu, smích je nesmírně prospěšný – nejen pro duši, ale i pro tělo. Může snížit stres a dokonce posílit váš imunitní systém.

Příště, až se budete smát, uvědomte si, že se nejen bavíte, ale také se staráte o své zdraví.

Nyní si vezmu chvíli na to, abych se podíval přímo do kamery a na chvíli se zastavil.

<Pauza a zavřete ústa>

Jak pokračuji ve čtení tohoto textu, cítím vzrušení.

Nemůžu se dočkat, až můj avatar ožije.

Sdílím své myšlenky, zatímco můj hlas zůstává klidný a tón zůstává pozváním.

Není fascinující, jak nám technologie umožňuje vytvářet digitální klony?

Můj avatar lze použít různými způsoby, od zlepšení komunikace až po poskytování personalizovaných zážitků.

Avatar, který bude vytvořen z tohoto záznamu, bude mocným nástrojem pro různé aplikace.

Ať už pro profesionální využití, zábavu nebo osobní interakce, tato digitální verze mě bude cenným přínosem.

Schopnost rozšířit mé působení do digitálního světa otevírá nespočet možností pro komunikaci a interakci.

<Pauza a zavřete ústa>

Jsem nadšený, jak tato technologie přivede můj digitální avatar k životu.

Podle těchto kroků budu schopen vytvořit něco inovativního a skutečně odrážejícího to, kdo jsem.

Připravte se na to, že zažijete něco skutečně výjimečného!

Představte si transformační dopad, kterého můžeme dosáhnout s tímto průkopnickým nápadem!

Jsme v čele změny a budoucnost přináší neomezené možnosti!

To není jen pokrok, to je revoluční krok vpřed!

Vytvořme skvělý avatar!

<Pauza a zavřete ústa>`,
    croatian: `<Gledajte izravno u kameru s prirodnim prijateljskim izrazom lica. Tijekom pauza, provjerite da su vam usne zatvorene.>

Bok! Danas ću pročitati kratak tekst kako bih pomogao u stvaranju digitalnog avatara koji me točno predstavlja.

Ovaj avatar će služiti kao moj digitalni blizanac, bilježeći moje jedinstvene pokrete, izraze lica i glas.

Sada ću pročitati kratak tekst za obuku svog avatara. Može trajati samo minutu, ali preporučuje se snimiti barem dvije minute.

Pridružite mi se dok stvaramo mog avatara!

Tijekom ovog snimanja održavat ću stalni tempo i jasnu artikulaciju.

Važno je da ostanem prirodan, opušten i dosljedan dok čitam tekst kako bih postigao što bolji rezultat.

Ovo osigurava da će avatar koji se stvara biti što realniji.

Dakle, dok snimam, pazim na svoje pokrete, koristim prirodne geste i pazim da su mi ruke opuštene i da ne prekrivaju lice.

Osjećam se sjajno dok čitam ovaj tekst. Donosim više izražajnosti svojim očima i iskren osmijeh na svoje lice.

Snimam najbolju verziju sebe, što će stvoriti još boljeg avatara.

<Zastanite i držite usne zatvorene>

Dopustite mi da podijelim s vama nekoliko zanimljivih spoznaja.

Jeste li znali da meditacija zapravo može povećati volumen sive tvari u vašem mozgu?

Dakle, kada uzmete trenutak da budete mirni, ne smirujete samo svoj um – doslovno povećavate svoj mozak!

S druge strane, smijeh je iznimno koristan – ne samo za dušu nego i za tijelo. Može smanjiti stres, pa čak i ojačati vaš imunološki sustav.

Dakle, sljedeći put kad se smijete, znajte da ne samo da se zabavljate; također brinete o svom zdravlju.

Sada ću uzeti trenutak da pogledam izravno u kameru i nakratko zastanem.

<Zastanite i držite usne zatvorene>

Dok nastavim čitati ovaj tekst, osjećam uzbuđenje.

Jedva čekam vidjeti kako moj avatar oživljava.

Dijelim svoje misli dok moj glas ostaje smiren, a ton poziva.

Nije li fascinantno kako nam tehnologija omogućuje stvaranje digitalnih klonova?

Moj avatar može se koristiti na različite načine, od poboljšanja komunikacije do pružanja personaliziranih iskustava.

Avatar koji će biti stvoren iz ovog snimka bit će snažan alat za različite primjene.

Bilo da se radi o profesionalnoj upotrebi, zabavi ili osobnim interakcijama, ova digitalna verzija mene bit će vrijedan resurs.

Mogućnost proširenja mog prisustva u digitalnom svijetu otvara bezbroj mogućnosti za komunikaciju i interakciju.

<Zastanite i držite usne zatvorene>

Uzbuđen sam vidjeti kako ova tehnologija oživljava moj digitalni avatar.

Slijedeći ove korake, moći ću stvoriti nešto inovativno i doista reflektirajuće o tome tko sam ja.

Pripremite se za doživljaj nečega doista izvanrednog!

Zamislite transformacijski učinak koji možemo postići ovom revolucionarnom idejom!

Nalazimo se na čelu promjene, a budućnost donosi neograničene mogućnosti!

Ovo nije samo napredak, ovo je revolucionarni korak naprijed!

Napravimo sjajan avatar!

<Zastanite i držite usne zatvorene>`,
    indonesian: `<Lihat langsung ke kamera dengan ekspresi ramah dan alami. Saat berhenti, pastikan bibir Anda tertutup.>

Halo! Hari ini, saya akan membaca naskah singkat untuk membantu menciptakan avatar digital yang secara akurat mewakili saya.

Avatar ini akan berfungsi sebagai kembaran digital, menangkap gerakan, ekspresi, dan suara unik saya.

Saya sekarang akan membaca naskah singkat untuk melatih avatar saya. Ini bisa sesingkat satu menit, tetapi disarankan untuk merekam setidaknya dua menit.

Bergabunglah dengan saya saat kita membuat avatar saya!

Selama rekaman ini, saya akan menjaga kecepatan yang stabil dan pengucapan yang jelas.

Sangat penting bagi saya untuk tetap alami, santai, dan konsisten saat membaca naskah untuk mencapai hasil terbaik.

Melakukan ini memastikan bahwa avatar yang dibuat akan se-realistis mungkin.

Jadi, saat merekam, saya memperhatikan gerakan saya, menggunakan gerakan alami, dan memastikan tangan saya tetap rileks dan tidak menutupi wajah saya.

Saya merasa hebat membaca naskah ini. Saya memberikan lebih banyak ekspresi pada mata saya dan senyum tulus di wajah saya.

Saya merekam versi terbaik dari diri saya, yang akan menciptakan avatar yang lebih baik lagi.

<Berhenti dan tutup bibir Anda>
Izinkan saya berbagi beberapa wawasan menarik dengan Anda.

Tahukah Anda bahwa meditasi sebenarnya dapat meningkatkan ukuran materi abu-abu di otak Anda?

Jadi, ketika Anda meluangkan waktu untuk diam, Anda tidak hanya menenangkan pikiran Anda — Anda benar-benar menumbuhkan otak Anda!

Di sisi lain, tertawa sangat bermanfaat — tidak hanya untuk jiwa tetapi juga untuk tubuh. Ini dapat mengurangi stres dan bahkan meningkatkan sistem kekebalan Anda.

Jadi, lain kali Anda tertawa, ketahuilah bahwa Anda tidak hanya bersenang-senang; Anda juga merawat kesehatan Anda.

Sekarang, saya akan meluangkan waktu untuk melihat langsung ke kamera dan berhenti sejenak.

<Berhenti dan tutup bibir Anda>
Saat saya terus membaca naskah ini, saya merasa bersemangat.

Saya tidak sabar untuk melihat avatar saya hidup.

Saya berbagi pemikiran saya, sementara suara saya tetap tenang dan nada saya mengundang.

Bukankah menarik bagaimana teknologi memungkinkan kita membuat klon digital?

Avatar saya dapat digunakan dengan berbagai cara, mulai dari meningkatkan komunikasi hingga memberikan pengalaman yang dipersonalisasi.

Avatar yang akan dibuat dari rekaman ini akan menjadi alat yang kuat untuk berbagai aplikasi.

Baik untuk keperluan profesional, hiburan, atau interaksi pribadi, versi digital dari diri saya ini akan menjadi aset yang berharga.

Kemampuan untuk memperluas kehadiran saya ke dunia digital membuka banyak kemungkinan untuk komunikasi dan keterlibatan.

<Berhenti dan tutup bibir Anda>
Saya bersemangat untuk melihat bagaimana teknologi ini menghidupkan avatar digital saya.

Dengan mengikuti langkah-langkah ini, saya akan dapat membuat sesuatu yang inovatif dan benar-benar mencerminkan siapa saya.

Bersiaplah untuk mengalami sesuatu yang benar-benar luar biasa!

Bayangkan dampak transformatif yang dapat kita capai dengan gagasan terobosan ini!

Kami berada di garis depan perubahan, dan masa depan memiliki kemungkinan tanpa batas!

Ini bukan hanya kemajuan, ini adalah langkah revolusioner ke depan!

Mari kita buat avatar yang hebat!

<Berhenti dan tutup bibir Anda>`,
    polish: `<Spójrz bezpośrednio w kamerę z naturalnym, przyjaznym wyrazem twarzy. Podczas pauz upewnij się, że twoje usta są zamknięte.>

Cześć! Dziś przeczytam krótki tekst, aby pomóc w stworzeniu cyfrowego awatara, który dokładnie mnie przedstawia.

Ten awatar będzie służył jako mój cyfrowy bliźniak, rejestrując moje unikalne ruchy, wyrazy twarzy i głos.

Teraz przeczytam krótki tekst, aby trenować mojego awatara. Może to trwać tylko minutę, ale zaleca się nagranie co najmniej dwóch minut.

Dołącz do mnie, tworząc mojego awatara!

Podczas tego nagrania utrzymam stałe tempo i wyraźną artykulację.

Ważne jest, abym pozostał naturalny, zrelaksowany i konsekwentny, czytając tekst, aby uzyskać jak najlepszy rezultat.

Robiąc to, zapewniam, że stworzony awatar będzie jak najbardziej realistyczny.

Dlatego podczas nagrywania zwracam uwagę na moje ruchy, używam naturalnych gestów i upewniam się, że moje ręce pozostają zrelaksowane i nie zasłaniają twarzy.

Czuję się świetnie, czytając ten tekst. Wnoszę więcej wyrazu do moich oczu i szczery uśmiech na twarzy.

Nagrywam najlepszą wersję siebie, co stworzy jeszcze lepszego awatara.

<Pauza i zamknij usta>
Pozwól, że podzielę się z tobą kilkoma fascynującymi faktami.

Czy wiedziałeś, że medytacja może faktycznie zwiększyć objętość szarej materii w twoim mózgu?

Więc kiedy poświęcasz chwilę na bycie spokojnym, nie tylko uspokajasz swój umysł — dosłownie rozwijasz swój mózg!

Z drugiej strony, śmiech jest niezwykle korzystny — nie tylko dla duszy, ale także dla ciała. Może obniżyć poziom stresu, a nawet wzmocnić twój układ odpornościowy.

Więc następnym razem, gdy będziesz się śmiać, wiedz, że nie tylko się bawisz, ale także dbasz o swoje zdrowie.

Teraz poświęcę chwilę, aby spojrzeć bezpośrednio w kamerę i zrobić krótką pauzę.

<Pauza i zamknij usta>
Kontynuując czytanie tego tekstu, czuję podekscytowanie.

Nie mogę się doczekać, aż mój awatar ożyje.

Dzielę się swoimi myślami, podczas gdy mój głos pozostaje spokojny, a ton zapraszający.

Czy to nie fascynujące, jak technologia pozwala nam tworzyć cyfrowe klony?

Mój awatar może być używany na wiele różnych sposobów, od ulepszania komunikacji po dostarczanie spersonalizowanych doświadczeń.

Awatar, który zostanie stworzony z tego nagrania, będzie potężnym narzędziem do różnych zastosowań.

Niezależnie od tego, czy jest to zastosowanie zawodowe, rozrywka czy interakcje osobiste, ta cyfrowa wersja mnie będzie cennym atutem.

Zdolność do rozszerzenia mojej obecności na świat cyfrowy otwiera niezliczone możliwości komunikacji i zaangażowania.

<Pauza i zamknij usta>
Jestem podekscytowany, aby zobaczyć, jak ta technologia ożywi mojego cyfrowego awatara.

Postępując zgodnie z tymi krokami, będę w stanie stworzyć coś innowacyjnego, co naprawdę odzwierciedla, kim jestem.

Przygotuj się na coś naprawdę wyjątkowego!

Wyobraź sobie transformacyjny wpływ, jaki możemy osiągnąć dzięki temu przełomowemu pomysłowi!

Jesteśmy na czele zmian, a przyszłość przynosi nieograniczone możliwości!

To nie tylko postęp, to rewolucyjny krok naprzód!

Stwórzmy wspaniałego awatara!

<Pauza i zamknij usta>`,
    romanian: `<Uită-te direct în cameră cu o expresie prietenoasă și naturală. În timpul pauzelor, asigură-te că buzele tale sunt închise.>

Salut! Astăzi voi citi un scurt scenariu pentru a ajuta la crearea unui avatar digital care să mă reprezinte cu acuratețe.

Acest avatar va servi drept dublu digital, capturând mișcările, expresiile și vocea mea unice.

Acum voi citi un scurt scenariu pentru a-mi antrena avatarul. Poate dura doar un minut, dar se recomandă să înregistrezi cel puțin două minute.

Alătură-te mie în timp ce creăm avatarul meu!

Pe parcursul acestei înregistrări, voi menține un ritm constant și o articulație clară.

Este esențial să rămân natural, relaxat și consecvent în timp ce citesc scenariul pentru a obține cel mai bun rezultat posibil.

Făcând acest lucru, mă asigur că avatarul creat va fi cât mai realist posibil.

Așadar, în timpul înregistrării, sunt atent la mișcările mele, folosesc gesturi naturale și mă asigur că mâinile mele rămân relaxate și nu îmi acoperă fața.

Mă simt grozav în timp ce citesc acest scenariu. Aduc mai multă expresivitate în ochii mei și un zâmbet sincer pe fața mea.

Filmez cea mai bună versiune a mea, ceea ce va crea un avatar și mai bun.

<Oprește-te și ține buzele închise>

Permite-mi să împărtășesc cu tine câteva fapte fascinante.

Știai că meditația poate crește efectiv dimensiunea materiei cenușii din creierul tău?

Așadar, când îți iei un moment să te liniștești, nu doar îți calmezi mintea — îți dezvolți efectiv creierul!

Pe de altă parte, râsul este incredibil de benefic — nu doar pentru suflet, ci și pentru corp. Poate reduce stresul și chiar îți poate întări sistemul imunitar.

Așadar, data viitoare când râzi, știi că nu doar te distrezi; ai grijă și de sănătatea ta.

Acum, îmi voi lua un moment să mă uit direct în cameră și să fac o scurtă pauză.

<Oprește-te și ține buzele închise>

Pe măsură ce continui să citesc acest scenariu, simt emoție.

De-abia aștept să văd cum avatarul meu prinde viață.

Îmi împărtășesc gândurile, în timp ce vocea mea rămâne calmă, iar tonul meu invitant.

Nu este fascinant cum tehnologia ne permite să creăm clone digitale?

Avatarul meu poate fi folosit în diverse moduri, de la îmbunătățirea comunicării la oferirea de experiențe personalizate.

Avatarul care va fi creat din această înregistrare va fi un instrument puternic pentru diverse aplicații.

Indiferent dacă este pentru utilizare profesională, divertisment sau interacțiuni personale, această versiune digitală a mea va fi un atu valoros.

Capacitatea de a-mi extinde prezența într-o lume digitală deschide nenumărate posibilități de comunicare și implicare.

<Oprește-te și ține buzele închise>

Sunt entuziasmat să văd cum această tehnologie va aduce la viață avatarul meu digital.

Urmând acești pași, voi putea crea ceva inovator și care reflectă cu adevărat cine sunt eu.

Pregătește-te să experimentezi ceva cu adevărat extraordinar!

Imaginează-ți impactul transformator pe care îl putem realiza cu această idee revoluționară!

Suntem în fruntea schimbării, iar viitorul oferă posibilități nelimitate!

Acesta nu este doar progres, este un pas revoluționar înainte!

Hai să facem un avatar grozav!

<Oprește-te și ține buzele închise>`,
    danish: `<Kig direkte ind i kameraet med et naturligt, venligt udtryk. Sørg for, at dine læber er lukkede under pauser.>

Hej! I dag vil jeg læse et kort manuskript for at hjælpe med at skabe en digital avatar, der nøjagtigt repræsenterer mig.

Denne avatar vil fungere som en digital tvilling og fange mine unikke bevægelser, udtryk og stemme.

Jeg vil nu læse et kort manuskript for at træne min avatar. Det kan være så kort som et minut, men det anbefales at optage mindst to minutter.

Kom og vær med til at skabe min avatar!

Under denne optagelse vil jeg holde et jævnt tempo og en tydelig artikulation.

Det er vigtigt, at jeg forbliver naturlig, afslappet og konsekvent, mens jeg læser manuskriptet for at opnå det bedst mulige resultat.

Ved at gøre dette sikrer jeg, at den avatar, der bliver skabt, er så realistisk som muligt.

Så mens jeg optager, er jeg opmærksom på mine bevægelser, bruger naturlige gestusser og sørger for, at mine hænder forbliver afslappede og ikke dækker mit ansigt.

Jeg har det godt, mens jeg læser dette manuskript. Jeg bringer mere udtryk til mine øjne og et ægte smil til mit ansigt.

Jeg optager den bedste version af mig selv, hvilket vil skabe en endnu bedre avatar.

<Pause og sørg for, at dine læber er lukkede>

Lad mig dele et par fascinerende indsigter med dig.

Vidste du, at meditation faktisk kan øge mængden af grå substans i din hjerne?

Så når du tager et øjeblik til at være stille, beroliger du ikke kun dit sind – du øger bogstaveligt talt din hjerne!

På en anden note er latter utroligt gavnlig – ikke kun for sjælen, men også for kroppen. Det kan reducere stress og endda styrke dit immunforsvar.

Så næste gang du griner, skal du vide, at du ikke bare har det sjovt; du passer også på dit helbred.

Nu vil jeg tage et øjeblik til at kigge direkte ind i kameraet og holde en kort pause.

<Pause og sørg for, at dine læber er lukkede>

Mens jeg fortsætter med at læse dette manuskript, føler jeg mig spændt.

Jeg kan ikke vente med at se min avatar komme til live.

Jeg deler mine tanker, mens min stemme forbliver rolig, og min tone er imødekommende.

Er det ikke fascinerende, hvordan teknologi giver os mulighed for at skabe digitale kloner?

Min avatar kan bruges på mange forskellige måder, fra at forbedre kommunikation til at levere personlige oplevelser.

Den avatar, der vil blive skabt fra denne optagelse, vil være et kraftfuldt værktøj til forskellige anvendelser.

Uanset om det er til professionelt brug, underholdning eller personlige interaktioner, vil denne digitale version af mig være en værdifuld ressource.

Evnen til at udvide min tilstedeværelse i en digital verden åbner op for utallige muligheder for kommunikation og engagement.

<Pause og sørg for, at dine læber er lukkede>

Jeg er spændt på at se, hvordan denne teknologi bringer min digitale avatar til live.

Ved at følge disse trin vil jeg være i stand til at skabe noget innovativt og virkelig afspejle, hvem jeg er.

Gør dig klar til at opleve noget virkelig exceptionelt!

Forestil dig den transformative indvirkning, vi kan opnå med denne banebrydende idé!

Vi står i spidsen for forandringer, og fremtiden byder på ubegrænsede muligheder!

Dette er ikke bare fremskridt, det er et revolutionerende skridt fremad!

Lad os skabe en fantastisk avatar!

<Pause og sørg for, at dine læber er lukkede>`,
    slovak: `<Pozrite sa priamo do kamery s prirodzeným, priateľským výrazom. Počas prestávok sa uistite, že máte zatvorené pery.>

Ahoj! Dnes budem čítať krátky text, aby som pomohol vytvoriť digitálneho avatara, ktorý ma presne reprezentuje.

Tento avatar bude slúžiť ako môj digitálny dvojník, zachytávajúci moje jedinečné pohyby, výrazy a hlas.

Teraz prečítam krátky text na tréning môjho avatara. Môže to trvať iba minútu, ale odporúča sa nahrávať aspoň dve minúty.

Pripojte sa ku mne, keď tvoríme môjho avatara!

Počas tohto nahrávania si budem udržiavať rovnomerné tempo a jasnú artikuláciu.

Je dôležité, aby som počas čítania textu zostal prirodzený, uvoľnený a konzistentný, aby som dosiahol čo najlepší výsledok.

Týmto sa zabezpečí, že vytvorený avatar bude čo najrealistickejší.

Takže počas nahrávania si dávam pozor na svoje pohyby, používam prirodzené gestá a uisťujem sa, že moje ruky sú uvoľnené a nezakrývajú mi tvár.

Cítim sa skvele pri čítaní tohto textu. Prinášam viac výrazu svojim očiam a úprimný úsmev na tvár.

Nahrávam najlepšiu verziu seba, čo vytvorí ešte lepšieho avatara.

<Pauza a majte zatvorené pery>

Dovoľte mi podeliť sa s vami o niekoľko fascinujúcich poznatkov.

Vedeli ste, že meditácia môže skutočne zvýšiť objem šedej hmoty vo vašom mozgu?

Takže keď si nájdete chvíľu na pokoj, nielenže upokojujete svoju myseľ — doslova si rozvíjate mozog!

Na druhej strane je smiech nesmierne prospešný — nielen pre dušu, ale aj pre telo. Môže znížiť stres a dokonca posilniť váš imunitný systém.

Takže nabudúce, keď sa budete smiať, vedzte, že sa nielen zabávate; staráte sa aj o svoje zdravie.

Teraz si vezmem chvíľu na to, aby som sa pozrel priamo do kamery a na chvíľu sa zastavil.

<Pauza a majte zatvorené pery>

Keď pokračujem v čítaní tohto textu, cítim vzrušenie.

Neviem sa dočkať, kedy môj avatar ožije.

Zdieľam svoje myšlienky, zatiaľ čo môj hlas zostáva pokojný a môj tón pozývajúci.

Nie je fascinujúce, ako nám technológia umožňuje vytvárať digitálne klony?

Môj avatar možno využiť rôznymi spôsobmi, od zlepšenia komunikácie až po poskytovanie personalizovaných zážitkov.

Avatar, ktorý bude vytvorený z tohto záznamu, bude mocným nástrojom pre rôzne aplikácie.

Či už na profesionálne využitie, zábavu alebo osobné interakcie, táto digitálna verzia mňa bude cenným prínosom.

Schopnosť rozšíriť moju prítomnosť do digitálneho sveta otvára nespočetné možnosti komunikácie a interakcie.

<Pauza a majte zatvorené pery>

Som nadšený, že uvidím, ako táto technológia oživí môj digitálny avatar.

Tým, že budem dodržiavať tieto kroky, budem schopný vytvoriť niečo inovatívne, čo skutočne odráža to, kto som.

Pripravte sa na to, že zažijete niečo skutočne výnimočné!

Predstavte si transformačný vplyv, ktorý môžeme dosiahnuť s týmto prelomovým nápadom!

Sme v popredí zmien a budúcnosť ponúka neobmedzené možnosti!

Toto nie je len pokrok, je to revolučný krok vpred!

Vytvorme skvelého avatara!

<Pauza a majte zatvorené pery>`,
    malay: `<Lihat terus ke arah kamera dengan ekspresi mesra dan semula jadi. Semasa berhenti, pastikan bibir anda tertutup.>

Hai! Hari ini saya akan membaca skrip pendek untuk membantu mencipta avatar digital yang mewakili saya dengan tepat.

Avatar ini akan berfungsi sebagai kembar digital, menangkap gerakan, ekspresi, dan suara unik saya.

Sekarang saya akan membaca skrip pendek untuk melatih avatar saya. Ia boleh selama satu minit, tetapi disyorkan untuk merakam sekurang-kurangnya dua minit.

Sertai saya semasa kita mencipta avatar saya!

Sepanjang rakaman ini, saya akan mengekalkan kelajuan yang tetap dan sebutan yang jelas.

Adalah penting untuk saya kekal semula jadi, santai, dan konsisten semasa membaca skrip untuk mencapai hasil yang terbaik.

Melakukan ini memastikan avatar yang dicipta akan kelihatan sebenar mungkin.

Jadi, semasa merakam, saya peka terhadap gerakan saya, menggunakan gerak isyarat semula jadi, dan memastikan tangan saya kekal santai serta tidak menutup wajah saya.

Saya berasa hebat membaca skrip ini. Saya membawa lebih banyak ekspresi kepada mata saya dan senyuman yang ikhlas di wajah saya.

Saya sedang merakam versi terbaik diri saya, yang akan menghasilkan avatar yang lebih baik lagi.

<Berhenti dan tutup bibir anda>
Izinkan saya berkongsi beberapa fakta menarik dengan anda.

Tahukah anda bahawa meditasi sebenarnya boleh meningkatkan saiz bahan kelabu di otak anda?

Jadi, apabila anda meluangkan masa untuk diam, anda bukan sahaja menenangkan minda anda — anda sebenarnya sedang mengembangkan otak anda!

Sebaliknya, ketawa sangat bermanfaat — bukan sahaja untuk jiwa tetapi juga untuk tubuh. Ia boleh mengurangkan tekanan dan bahkan meningkatkan sistem imun anda.

Jadi, lain kali anda ketawa, ketahuilah bahawa anda bukan sahaja berseronok; anda juga menjaga kesihatan anda.

Sekarang, saya akan meluangkan masa untuk melihat terus ke kamera dan berhenti sejenak.

<Berhenti dan tutup bibir anda>
Semasa saya terus membaca skrip ini, saya berasa teruja.

Saya tidak sabar untuk melihat avatar saya menjadi hidup.

Saya berkongsi fikiran saya, manakala suara saya tetap tenang dan nada saya mesra.

Bukankah menakjubkan bagaimana teknologi membolehkan kita mencipta klon digital?

Avatar saya boleh digunakan dalam pelbagai cara, dari meningkatkan komunikasi hingga memberikan pengalaman yang diperibadikan.

Avatar yang akan dihasilkan daripada rakaman ini akan menjadi alat yang berkuasa untuk pelbagai aplikasi.

Sama ada untuk kegunaan profesional, hiburan, atau interaksi peribadi, versi digital diri saya ini akan menjadi aset yang berharga.

Keupayaan untuk mengembangkan kehadiran saya ke dalam dunia digital membuka banyak kemungkinan untuk komunikasi dan penglibatan.

<Berhenti dan tutup bibir anda>
Saya teruja untuk melihat bagaimana teknologi ini akan menghidupkan avatar digital saya.

Dengan mengikuti langkah-langkah ini, saya akan dapat mencipta sesuatu yang inovatif dan benar-benar mencerminkan siapa diri saya.

Bersedia untuk mengalami sesuatu yang benar-benar luar biasa!

Bayangkan kesan transformatif yang boleh kita capai dengan idea terobosan ini!

Kita berada di barisan hadapan perubahan, dan masa depan menawarkan kemungkinan yang tidak terhad!

Ini bukan sekadar kemajuan, ini adalah langkah revolusioner ke hadapan!

Mari kita cipta avatar yang hebat!

<Berhenti dan tutup bibir anda>`,
};

const premiumPlusAvatarTextByLanguage: Record<SupportedLanguage, string> = {
    english: `<Look directly into the camera with a natural friendly expression> 

Hello! Today, I’ll be reading a short script to help create a digital avatar that accurately represents me. 

This avatar will serve as a digital clone, capturing my unique expressions and voice. 

I will now read a few minutes’ script. It will take 5 minutes to record. 

Please join me as we create my Avatar! 

<Pause and keep your lips closed> 

During this recording, I’ll maintain a steady pace and clear articulation. It is essential that I remain natural, relaxed, and consistent throughout this reading to achieve the best possible result. 

This ensures that the Avatar created will be as realistic and authentic as possible. 

So I am mindful of my movements, using natural gestures, making sure my hands remain relaxed and do not cover my face. 

I feel great reading through this script. 

I bring more expression to my eyes and a genuine smile to my face. I am shooting the best version of myself, which will create an even better avatar. 

<Pause and keep your lips closed> 

Let me share a few fascinating insights with you. 

Did you know that meditation has been shown to actually increase the size of your brain’s gray matter? 

So, when you take a moment to be still, you’re not just calming your mind—you’re literally growing your brain! 

On a different note, laughter is incredibly beneficial—not just for the soul but also for the body. It can lower stress and even boost your immune system. 

So, next time you laugh, know that you’re not just having fun; you’re also taking care of your health. 

Now, I’ll take a moment to look directly into the camera and pause briefly. 

<Pause and keep your lips closed> 

As I continue to read this script, I'm feeling excited. I can't wait to see my avatar come to life. 

I'm sharing my thoughts, while my voice remains calm and my tone remains inviting. 

Isn’t it fascinating how technology allows us to create digital clones? 

My avatar can be used in a variety of ways, from enhancing communication to providing personalized experiences. 

The avatar that will be created from this footage will be a powerful tool for various applications. 

Whether it’s for professional use, entertainment, or personal interactions, this digital version of myself will be a valuable asset. 

The ability to extend my presence into a digital realm opens up countless possibilities for communication and engagement. 

<Pause and keep your lips closed> 

I’m excited to see how this technology brings my digital avatar to life. 

By following these steps, I’ll be able to create something innovative and truly reflective of who I am. 

Get ready to experience something incredible! 

Imagine the impact we can achieve with this groundbreaking technology! 

We’re at the forefront of change, and the future holds limitless possibilities! 

This isn’t just progress, it’s a huge step forward! 

Let’s make a great Avatar! 

<Pause and keep your lips closed> 

Let me also reflect on how far technology has come. 

Not too long ago, creating digital versions of ourselves was something out of science fiction. 

Now, this technology is real, and it's growing fast. Imagine a future where our Avatars can help us solve problems.

These digital versions of ourselves could soon be more than just copies—they could enhance how we interact with the world.

Isn't that amazing?

Making an avatar isn’t just about creating a digital version of ourselves; it’s about shaping a future where our real and digital lives blend together in exciting ways.

<Pause and keep your lips closed> 

Every moment of this recording helps build my new digital Avatar.

It’s an exciting journey, and I can’t wait to see what comes next.

And that’s it. I’m excited to see how my Avatar turns out.
`,
    spanish: `<Mirar directamente a la cámara con una expresión natural y amigable>

¡Hola! Hoy, leeré un guion corto para ayudar a crear un avatar digital que me represente con precisión.

Este avatar servirá como un clon digital, capturando mis expresiones y mi voz únicas.

Ahora leeré un guion de unos pocos minutos. Tomará 5 minutos grabarlo.

¡Únete a mí mientras creamos mi Avatar!

<Pausa y mantén los labios cerrados>

Durante esta grabación, mantendré un ritmo constante y una articulación clara. Es esencial que permanezca natural, relajado y consistente a lo largo de esta lectura para lograr el mejor resultado posible.

Esto asegura que el Avatar creado sea lo más realista y auténtico posible.

Así que soy consciente de mis movimientos, usando gestos naturales, asegurándome de que mis manos permanezcan relajadas y no cubran mi rostro.

Me siento genial leyendo este guion.

Le doy más expresión a mis ojos y una sonrisa genuina a mi rostro. Estoy filmando la mejor versión de mí mismo, lo que creará un avatar aún mejor.

<Pausa y mantén los labios cerrados>

Permíteme compartir algunos datos fascinantes contigo.

¿Sabías que se ha demostrado que la meditación en realidad aumenta el tamaño de la materia gris de tu cerebro?

Así que, cuando tomas un momento para estar quieto, no solo estás calmando tu mente, ¡literalmente estás haciendo crecer tu cerebro!

Por otro lado, la risa es increíblemente beneficiosa, no solo para el alma, sino también para el cuerpo. Puede reducir el estrés e incluso fortalecer tu sistema inmunológico.

Así que, la próxima vez que rías, recuerda que no solo te estás divirtiendo, ¡también estás cuidando tu salud!

Ahora, tomaré un momento para mirar directamente a la cámara y hacer una breve pausa.

<Pausa y mantén los labios cerrados>

Mientras continúo leyendo este guion, me siento emocionado. No puedo esperar a ver cómo cobra vida mi avatar.

Estoy compartiendo mis pensamientos, mientras mi voz permanece calmada y mi tono sigue siendo acogedor.

¿No es fascinante cómo la tecnología nos permite crear clones digitales?

Mi avatar se puede usar de diversas maneras, desde mejorar la comunicación hasta brindar experiencias personalizadas.

El avatar que se creará a partir de esta grabación será una herramienta poderosa para varias aplicaciones.

Ya sea para uso profesional, entretenimiento o interacciones personales, esta versión digital de mí será un recurso valioso.

La capacidad de extender mi presencia al reino digital abre infinitas posibilidades para la comunicación y el compromiso.

<Pausa y mantén los labios cerrados>

Estoy emocionado de ver cómo esta tecnología da vida a mi avatar digital.

Siguiendo estos pasos, podré crear algo innovador y verdaderamente reflejante de quién soy.

¡Prepárate para experimentar algo increíble!

¡Imagina el impacto que podemos lograr con esta tecnología revolucionaria!

¡Estamos a la vanguardia del cambio, y el futuro tiene posibilidades ilimitadas!

¡Esto no es solo progreso, es un gran paso adelante!

¡Hagamos un gran Avatar!

<Pausa y mantén los labios cerrados>

Déjame también reflexionar sobre cuánto ha avanzado la tecnología.

Hace no mucho tiempo, crear versiones digitales de nosotros mismos era algo de la ciencia ficción.

Ahora, esta tecnología es real, y está creciendo rápidamente. Imagina un futuro en el que nuestros Avatares nos ayuden a resolver problemas.

Estas versiones digitales de nosotros mismos pronto podrían ser más que solo copias: podrían mejorar la forma en que interactuamos con el mundo.

¿No es increíble?

Hacer un avatar no se trata solo de crear una versión digital de nosotros mismos; se trata de moldear un futuro en el que nuestras vidas reales y digitales se mezclen de maneras emocionantes.

<Pausa y mantén los labios cerrados> 

Cada momento de esta grabación ayuda a construir mi nuevo Avatar digital.

Es un viaje emocionante y no puedo esperar a ver qué sigue. 

Y eso es todo. Estoy emocionado de ver cómo queda mi Avatar.`,
    italian: `<Guarda direttamente nella fotocamera con un'espressione naturale e amichevole>

Ciao! Oggi leggerò un breve copione per aiutare a creare un avatar digitale che mi rappresenti con precisione.

Questo avatar servirà come un clone digitale, catturando le mie espressioni e la mia voce uniche.

Ora leggerò un copione di qualche minuto. Ci vorranno 5 minuti per registrarlo.

Unisciti a me mentre creiamo il mio Avatar!

<Pausa e mantieni le labbra chiuse>

Durante questa registrazione, manterrò un ritmo costante e un'articolazione chiara. È essenziale che io rimanga naturale, rilassato e coerente durante questa lettura per ottenere il miglior risultato possibile.

Questo garantisce che l'Avatar creato sarà il più realistico e autentico possibile.

Quindi faccio attenzione ai miei movimenti, usando gesti naturali, assicurandomi che le mie mani rimangano rilassate e non coprano il mio volto.

Mi sento benissimo a leggere questo copione.

Porto più espressione ai miei occhi e un sorriso genuino al mio volto. Sto girando la migliore versione di me stesso, che creerà un avatar ancora migliore.

<Pausa e mantieni le labbra chiuse>

Lascia che ti condivida alcune curiosità interessanti.

Sapevi che è stato dimostrato che la meditazione aumenta effettivamente la dimensione della materia grigia del tuo cervello?

Quindi, quando ti prendi un momento per stare fermo, non stai solo calmando la mente, ma stai letteralmente facendo crescere il tuo cervello!

Inoltre, ridere è incredibilmente benefico, non solo per l'anima ma anche per il corpo. Può ridurre lo stress e persino rafforzare il tuo sistema immunitario.

Quindi, la prossima volta che ridi, sappi che non ti stai solo divertendo, ma stai anche prendendoti cura della tua salute.

Ora prenderò un momento per guardare direttamente nella fotocamera e fare una breve pausa.

<Pausa e mantieni le labbra chiuse>

Mentre continuo a leggere questo copione, mi sento entusiasta. Non vedo l'ora di vedere il mio avatar prendere vita.

Sto condividendo i miei pensieri, mentre la mia voce rimane calma e il mio tono continua ad essere accogliente.

Non è affascinante come la tecnologia ci permetta di creare cloni digitali?

Il mio avatar può essere utilizzato in vari modi, dall'ottimizzazione della comunicazione alla fornitura di esperienze personalizzate.

L'avatar che verrà creato da questa registrazione sarà uno strumento potente per molteplici applicazioni.

Che sia per uso professionale, intrattenimento o interazioni personali, questa versione digitale di me sarà una risorsa preziosa.

La capacità di estendere la mia presenza in un regno digitale apre infinite possibilità per la comunicazione e il coinvolgimento.

<Pausa e mantieni le labbra chiuse>

Sono entusiasta di vedere come questa tecnologia darà vita al mio avatar digitale.

Seguendo questi passaggi, sarò in grado di creare qualcosa di innovativo e davvero riflettente di chi sono.

Preparati a vivere qualcosa di incredibile!

Immagina l'impatto che possiamo ottenere con questa tecnologia rivoluzionaria!

Siamo all'avanguardia del cambiamento, e il futuro offre possibilità illimitate!

Questo non è solo progresso, è un grande passo avanti!

Facciamo un grande Avatar!

<Pausa e mantieni le labbra chiuse>

Lasciami anche riflettere su quanto è avanzata la tecnologia.

Non molto tempo fa, creare versioni digitali di noi stessi era qualcosa di fantascienza.

Ora, questa tecnologia è reale, e sta crescendo rapidamente. Immagina un futuro in cui i nostri Avatar ci aiutano a risolvere problemi.

Queste versioni digitali di noi stessi potrebbero presto essere più di semplici copie: potrebbero migliorare il modo in cui interagiamo con il mondo.

Non è incredibile?

Creare un avatar non significa solo realizzare una versione digitale di noi stessi; significa plasmare un futuro in cui le nostre vite reali e digitali si fondono in modi entusiasmanti.

<Pausa e tieni le labbra chiuse> 

Ogni momento di questa registrazione aiuta a costruire il mio nuovo Avatar digitale. 

È un viaggio entusiasmante e non vedo l'ora di vedere cosa succederà dopo. 

E questo è tutto. Sono emozionato di vedere come sarà il mio Avatar.`,
    swedish: `<Titta direkt in i kameran med ett naturligt och vänligt uttryck>

Hej! Idag ska jag läsa ett kort manus för att hjälpa till att skapa en digital avatar som exakt representerar mig.

Denna avatar kommer att fungera som en digital klon, och fånga mina unika uttryck och min röst.

Nu ska jag läsa ett manus på några minuter. Det tar 5 minuter att spela in.

Följ med mig medan vi skapar min Avatar!

<Paus och håll läpparna stängda>

Under denna inspelning kommer jag att hålla ett jämnt tempo och artikulera tydligt. Det är viktigt att jag förblir naturlig, avslappnad och konsekvent under hela läsningen för att uppnå bästa möjliga resultat.

Detta säkerställer att den Avatar som skapas blir så realistisk och autentisk som möjligt.

Så jag är medveten om mina rörelser, använder naturliga gester och ser till att mina händer förblir avslappnade och inte täcker mitt ansikte.

Jag känner mig fantastisk när jag läser det här manuset.

Jag ger mer uttryck åt mina ögon och ett genuint leende åt mitt ansikte. Jag filmar den bästa versionen av mig själv, vilket kommer att skapa en ännu bättre avatar.

<Paus och håll läpparna stängda>

Låt mig dela några fascinerande insikter med dig.

Visste du att meditation faktiskt har visat sig öka mängden grå substans i din hjärna?

Så när du tar en stund för att vara stilla, lugnar du inte bara ditt sinne – du bokstavligen växer din hjärna!

På en annan not, skratt är otroligt fördelaktigt – inte bara för själen utan även för kroppen. Det kan minska stress och till och med stärka ditt immunförsvar.

Så nästa gång du skrattar, vet att du inte bara har roligt; du tar också hand om din hälsa.

Nu tar jag en stund för att titta direkt in i kameran och göra en kort paus.

<Paus och håll läpparna stängda>

När jag fortsätter att läsa detta manus känner jag mig uppspelt. Jag kan knappt vänta på att se min avatar komma till liv.

Jag delar mina tankar, medan min röst förblir lugn och min ton förblir inbjudande.

Är det inte fascinerande hur tekniken gör det möjligt för oss att skapa digitala kloner?

Min avatar kan användas på olika sätt, från att förbättra kommunikationen till att ge personliga upplevelser.

Den avatar som kommer att skapas från detta material kommer att vara ett kraftfullt verktyg för olika tillämpningar.

Oavsett om det är för professionellt bruk, underhållning eller personliga interaktioner, kommer denna digitala version av mig att vara en värdefull tillgång.

Förmågan att utöka min närvaro i den digitala världen öppnar upp otaliga möjligheter för kommunikation och engagemang.

<Paus och håll läpparna stängda>

Jag är exalterad över att se hur denna teknik ger liv åt min digitala avatar.

Genom att följa dessa steg kommer jag att kunna skapa något innovativt och verkligen reflekterande av vem jag är.

Förbered dig på att uppleva något otroligt!

Föreställ dig vilken påverkan vi kan uppnå med denna banbrytande teknik!

Vi är i framkanten av förändring, och framtiden rymmer oändliga möjligheter!

Detta är inte bara framsteg, det är ett stort steg framåt!

Låt oss skapa en fantastisk Avatar!

<Paus och håll läpparna stängda>

Låt mig också reflektera över hur långt tekniken har kommit.

För inte så länge sedan var att skapa digitala versioner av oss själva något ur science fiction.

Nu är denna teknik verklig och växer snabbt. Föreställ dig en framtid där våra Avatars hjälper oss att lösa problem.

Dessa digitala versioner av oss själva kan snart vara mer än bara kopior – de kan förbättra hur vi interagerar med världen.

Är det inte fantastiskt?

Att skapa en avatar handlar inte bara om att skapa en digital version av oss själva; det handlar om att forma en framtid där våra verkliga och digitala liv smälter samman på spännande sätt.

<Pausa och håll läpparna stängda> 

Varje ögonblick av denna inspelning hjälper till att bygga min nya digitala Avatar. 

Det är en spännande resa och jag ser fram emot att se vad som kommer härnäst. 

Och det är allt. Jag är spänd på att se hur min Avatar blir.`,
    german: `<Schaue direkt in die Kamera mit einem natürlichen, freundlichen Ausdruck> 

Hallo! Heute werde ich ein kurzes Skript lesen, um dabei zu helfen, einen digitalen Avatar zu erstellen, der mich genau repräsentiert. 

Dieser Avatar wird als digitaler Klon dienen und meine einzigartigen Ausdrücke und meine Stimme erfassen. 

Ich werde jetzt ein mehrminütiges Skript vorlesen. Es wird 5 Minuten dauern, es aufzunehmen. 

Begleite mich, während wir meinen Avatar erstellen! 

<Pause und halte die Lippen geschlossen> 

Während dieser Aufnahme werde ich ein gleichmäßiges Tempo beibehalten und klar artikulieren. Es ist wichtig, dass ich während des gesamten Lesens natürlich, entspannt und konsistent bleibe, um das bestmögliche Ergebnis zu erzielen. 

Dies stellt sicher, dass der erstellte Avatar so realistisch und authentisch wie möglich ist. 

Ich achte also auf meine Bewegungen, verwende natürliche Gesten und stelle sicher, dass meine Hände entspannt bleiben und mein Gesicht nicht verdecken. 

Es fühlt sich großartig an, dieses Skript zu lesen. 

Ich bringe mehr Ausdruck in meine Augen und ein echtes Lächeln in mein Gesicht. Ich erschaffe die beste Version von mir selbst, was einen noch besseren Avatar erzeugen wird. 

<Pause und halte die Lippen geschlossen> 

Lass mich dir ein paar faszinierende Einblicke geben. 

Wusstest du, dass Meditation tatsächlich die Größe der grauen Substanz in deinem Gehirn erhöhen kann? 

Wenn du also einen Moment still bleibst, beruhigst du nicht nur deinen Geist – du vergrößerst buchstäblich dein Gehirn! 

Und noch etwas: Lachen ist unglaublich vorteilhaft – nicht nur für die Seele, sondern auch für den Körper. Es kann Stress reduzieren und sogar dein Immunsystem stärken. 

Also, wenn du das nächste Mal lachst, denke daran, dass du nicht nur Spaß hast, sondern auch deiner Gesundheit etwas Gutes tust. 

Jetzt werde ich kurz direkt in die Kamera schauen und eine kleine Pause machen. 

<Pause und halte die Lippen geschlossen> 

Während ich dieses Skript weiterlese, fühle ich mich aufgeregt. Ich kann es kaum erwarten, meinen Avatar zum Leben zu erwecken. 

Ich teile meine Gedanken, während meine Stimme ruhig und mein Ton einladend bleibt. 

Ist es nicht faszinierend, wie uns die Technologie ermöglicht, digitale Klone zu erstellen? 

Mein Avatar kann auf verschiedene Weise genutzt werden, um die Kommunikation zu verbessern oder personalisierte Erlebnisse zu bieten. 

Der Avatar, der aus diesem Material erstellt wird, wird ein leistungsstarkes Werkzeug für verschiedene Anwendungen sein. 

Ob für berufliche Zwecke, Unterhaltung oder persönliche Interaktionen – diese digitale Version von mir wird ein wertvolles Gut sein. 

Die Möglichkeit, meine Präsenz in die digitale Welt zu erweitern, eröffnet unzählige Möglichkeiten für Kommunikation und Engagement. 

<Pause und halte die Lippen geschlossen> 

Ich bin gespannt, wie diese Technologie meinen digitalen Avatar zum Leben erweckt. 

Durch die Befolgung dieser Schritte werde ich in der Lage sein, etwas Innovatives zu schaffen, das wirklich widerspiegelt, wer ich bin. 

Mach dich bereit, etwas Unglaubliches zu erleben! 

Stell dir vor, welche Wirkung wir mit dieser bahnbrechenden Technologie erzielen können! 

Wir stehen an der Spitze des Wandels, und die Zukunft hält unbegrenzte Möglichkeiten bereit! 

Das ist nicht nur Fortschritt, es ist ein großer Schritt nach vorne! 

Lass uns einen großartigen Avatar erschaffen! 

<Pause und halte die Lippen geschlossen> 

Lass mich auch reflektieren, wie weit die Technologie gekommen ist. 

Vor nicht allzu langer Zeit war das Erstellen digitaler Versionen von uns selbst etwas aus der Science-Fiction. 

Jetzt ist diese Technologie real und wächst schnell. Stell dir eine Zukunft vor, in der unsere Avatare uns bei der Lösung von Problemen helfen. 

Diese digitalen Versionen von uns könnten bald mehr sein als nur Kopien – sie könnten verbessern, wie wir mit der Welt interagieren. 

Ist das nicht erstaunlich? 

Einen Avatar zu erschaffen, bedeutet nicht nur, eine digitale Version von uns selbst zu erstellen; es geht darum, eine Zukunft zu gestalten, in der unser reales und digitales Leben auf aufregende Weise verschmilzt. 

<Pause und halte deine Lippen geschlossen> 

Jeder Moment dieser Aufnahme hilft, meinen neuen digitalen Avatar aufzubauen. 

Es ist eine aufregende Reise und ich kann es kaum erwarten, zu sehen, was als Nächstes kommt.

Und das war's. Ich bin gespannt, wie mein Avatar wird.`,
    french: `<Regarde directement la caméra avec une expression naturelle et amicale> 

Bonjour ! Aujourd'hui, je vais lire un court script pour aider à créer un avatar numérique qui me représente fidèlement. 

Cet avatar servira de clone numérique, capturant mes expressions uniques et ma voix. 

Je vais maintenant lire un script de quelques minutes. Il faudra 5 minutes pour l'enregistrer. 

Rejoignez-moi pendant que nous créons mon avatar ! 

<Pause et garde les lèvres fermées> 

Pendant cet enregistrement, je vais garder un rythme constant et articuler clairement. Il est essentiel que je reste naturel, détendu et cohérent tout au long de cette lecture pour obtenir le meilleur résultat possible. 

Cela garantit que l'avatar créé sera aussi réaliste et authentique que possible. 

Je fais donc attention à mes mouvements, en utilisant des gestes naturels, en veillant à ce que mes mains restent détendues et ne couvrent pas mon visage. 

Je me sens très bien en lisant ce script. 

J'apporte plus d'expression à mes yeux et un sourire sincère à mon visage. Je filme la meilleure version de moi-même, ce qui créera un avatar encore meilleur. 

<Pause et garde les lèvres fermées> 

Laisse-moi te partager quelques informations fascinantes. 

Savais-tu que la méditation a été prouvée pour réellement augmenter la taille de la matière grise de ton cerveau ? 

Donc, lorsque tu prends un moment pour être immobile, tu ne fais pas que calmer ton esprit — tu fais littéralement grandir ton cerveau ! 

Par ailleurs, le rire est incroyablement bénéfique — non seulement pour l’âme, mais aussi pour le corps. Il peut réduire le stress et même renforcer ton système immunitaire. 

Donc, la prochaine fois que tu ris, sache que tu ne fais pas que t’amuser ; tu prends aussi soin de ta santé. 

Maintenant, je vais prendre un moment pour regarder directement la caméra et faire une brève pause. 

<Pause et garde les lèvres fermées> 

Alors que je continue à lire ce script, je me sens excité. J'ai hâte de voir mon avatar prendre vie. 

Je partage mes pensées, tandis que ma voix reste calme et mon ton reste invitant. 

N’est-ce pas fascinant de voir comment la technologie nous permet de créer des clones numériques ? 

Mon avatar pourra être utilisé de différentes manières, pour améliorer la communication ou fournir des expériences personnalisées. 

L'avatar qui sera créé à partir de ces images sera un outil puissant pour diverses applications. 

Que ce soit pour un usage professionnel, le divertissement ou des interactions personnelles, cette version numérique de moi-même sera un atout précieux. 

La capacité à étendre ma présence dans le domaine numérique ouvre d'innombrables possibilités de communication et d'engagement. 

<Pause et garde les lèvres fermées> 

Je suis impatient de voir comment cette technologie donnera vie à mon avatar numérique. 

En suivant ces étapes, je vais pouvoir créer quelque chose d'innovant et vraiment représentatif de qui je suis. 

Prépare-toi à vivre quelque chose d’incroyable ! 

Imagine l’impact que nous pouvons obtenir avec cette technologie révolutionnaire ! 

Nous sommes à l'avant-garde du changement, et l'avenir offre des possibilités illimitées ! 

Ce n'est pas seulement un progrès, c'est un grand pas en avant ! 

Faisons un super avatar ! 

<Pause et garde les lèvres fermées> 

Laisse-moi aussi réfléchir à l'évolution de la technologie. 

Il n'y a pas si longtemps, créer des versions numériques de nous-mêmes relevait de la science-fiction. 

Aujourd'hui, cette technologie est réelle et elle se développe rapidement. Imagine un avenir où nos avatars peuvent nous aider à résoudre des problèmes. 

Ces versions numériques de nous-mêmes pourraient bientôt être plus que de simples copies — elles pourraient améliorer notre façon d'interagir avec le monde. 

N'est-ce pas incroyable ? 

Créer un avatar, ce n'est pas seulement créer une version numérique de nous-mêmes ; c'est façonner un avenir où nos vies réelles et numériques se mêlent de manière passionnante. 

<Pause et garde les lèvres fermées>

Chaque moment de cet enregistrement contribue à créer mon nouveau Avatar numérique.

C'est un voyage passionnant et j'ai hâte de voir ce qui va suivre. 

Et c'est tout. Je suis impatient de voir à quoi ressemblera mon Avatar.`,
    dutch: `<Kijk recht in de camera met een natuurlijke, vriendelijke uitstraling> 

Hallo! Vandaag ga ik een kort script voorlezen om te helpen bij het creëren van een digitaal avatar die mij nauwkeurig vertegenwoordigt. 

Dit avatar zal dienen als een digitale kloon, die mijn unieke uitdrukkingen en stem vastlegt. 

Ik ga nu een script van een paar minuten voorlezen. Het zal 5 minuten duren om op te nemen. 

Doe met me mee terwijl we mijn avatar creëren! 

<Pauze en houd je lippen gesloten> 

Tijdens deze opname zal ik een constante snelheid en duidelijke articulatie aanhouden. Het is essentieel dat ik gedurende deze lezing natuurlijk, ontspannen en consistent blijf om het best mogelijke resultaat te bereiken. 

Dit zorgt ervoor dat de gemaakte avatar zo realistisch en authentiek mogelijk zal zijn. 

Dus ben ik me bewust van mijn bewegingen, gebruik ik natuurlijke gebaren, en zorg ik ervoor dat mijn handen ontspannen blijven en mijn gezicht niet bedekken. 

Ik voel me geweldig terwijl ik dit script doorlees. 

Ik breng meer expressie in mijn ogen en een oprechte glimlach op mijn gezicht. Ik maak de beste versie van mezelf, wat een nog betere avatar zal creëren. 

<Pauze en houd je lippen gesloten> 

Laat me een paar fascinerende inzichten met je delen. 

Wist je dat meditatie de grootte van de grijze stof in je hersenen daadwerkelijk kan vergroten? 

Dus wanneer je een moment neemt om stil te zijn, kalmeer je niet alleen je geest - je laat je hersenen letterlijk groeien! 

Aan de andere kant is lachen ongelooflijk gunstig - niet alleen voor de ziel, maar ook voor het lichaam. Het kan stress verlagen en zelfs je immuunsysteem een boost geven. 

Dus, de volgende keer dat je lacht, weet dat je niet alleen plezier hebt; je zorgt ook voor je gezondheid. 

Nu ga ik een moment nemen om recht in de camera te kijken en kort te pauzeren. 

<Pauze en houd je lippen gesloten> 

Terwijl ik dit script blijf lezen, voel ik me opgewonden. Ik kan niet wachten om mijn avatar tot leven te zien komen. 

Ik deel mijn gedachten, terwijl mijn stem kalm blijft en mijn toon uitnodigend is. 

Is het niet fascinerend hoe technologie ons in staat stelt digitale kloons te creëren? 

Mijn avatar kan op verschillende manieren worden gebruikt, van het verbeteren van communicatie tot het bieden van gepersonaliseerde ervaringen. 

De avatar die uit deze beelden zal worden gemaakt, zal een krachtig hulpmiddel zijn voor verschillende toepassingen. 

Of het nu voor professioneel gebruik is, entertainment of persoonlijke interacties, deze digitale versie van mezelf zal een waardevol bezit zijn. 

De mogelijkheid om mijn aanwezigheid in een digitale wereld uit te breiden, opent talloze mogelijkheden voor communicatie en betrokkenheid. 

<Pauze en houd je lippen gesloten> 

Ik ben enthousiast om te zien hoe deze technologie mijn digitale avatar tot leven brengt. 

Door deze stappen te volgen, kan ik iets innovatiefs creëren dat echt een afspiegeling is van wie ik ben. 

Maak je klaar om iets ongelooflijks te ervaren! 

Stel je de impact voor die we kunnen bereiken met deze baanbrekende technologie! 

We staan aan de voorhoede van verandering, en de toekomst biedt onbeperkte mogelijkheden! 

Dit is niet alleen vooruitgang, het is een grote stap vooruit! 

Laten we een geweldige avatar maken! 

<Pauze en houd je lippen gesloten> 

Laat me ook reflecteren op hoe ver de technologie is gekomen. 

Nog niet zo lang geleden was het creëren van digitale versies van onszelf iets uit de sciencefiction. 

Nu is deze technologie echt en groeit ze snel. Stel je een toekomst voor waarin onze avatars ons kunnen helpen problemen op te lossen. 

Deze digitale versies van onszelf zouden binnenkort meer kunnen zijn dan alleen kopieën - ze zouden kunnen verbeteren hoe we met de wereld omgaan. 

Is dat niet geweldig? 

Een avatar maken is niet alleen een digitale versie van onszelf creëren; het gaat erom een toekomst te vormen waarin ons echte en digitale leven op opwindende manieren samenkomen. 

<Pauzeer en houd je lippen gesloten>

Elk moment van deze opname helpt bij het bouwen van mijn nieuwe digitale Avatar. 

Het is een spannende reis en ik kan niet wachten om te zien wat er hierna komt. 

En dat is alles. Ik ben benieuwd naar hoe mijn Avatar eruit komt te zien.`,
    filipino: `<Tumingin nang direkta sa kamera na may natural na magiliw na ekspresyon> 

Hello! Ngayon, magbabasa ako ng maikling script upang makatulong sa paglikha ng isang digital avatar na tumpak na kumakatawan sa akin. 

Ang avatar na ito ay magsisilbing digital clone, na kumukuha ng aking natatanging ekspresyon at boses. 

Ngayon, magbabasa ako ng ilang minutong script. Magtatagal ito ng 5 minuto para maitala. 

Sumali ka sa akin habang nililikha natin ang aking Avatar! 

<Pahinga at panatilihing nakasara ang iyong mga labi> 

Sa panahon ng pagtatala na ito, pananatiliin ko ang isang steady na bilis at malinaw na pagbigkas. Mahalaga na manatili akong natural, relaxed, at consistent sa buong pagbabasang ito upang makamit ang pinakamahusay na posibleng resulta. 

Tinitiyak nito na ang Avatar na nilikha ay magiging kasing-realistiko at tunay hangga't maaari. 

Kaya't ako ay maingat sa aking mga galaw, gumagamit ng natural na kilos, tinitiyak na ang aking mga kamay ay nananatiling relaxed at hindi natatakpan ang aking mukha. 

Masaya akong magbasa sa script na ito. 

Nagdadala ako ng higit na ekspresyon sa aking mga mata at isang taos-pusong ngiti sa aking mukha. Kinukuha ko ang pinakamagandang bersyon ng aking sarili, na lilikha ng mas magandang avatar. 

<Pahinga at panatilihing nakasara ang iyong mga labi> 

Hayaan mo akong ibahagi ang ilang mga kamangha-manghang pananaw sa iyo. 

Alam mo ba na ang meditasyon ay napatunayan na talagang nagdaragdag sa laki ng gray matter ng iyong utak? 

Kaya, kapag kumuha ka ng isang sandali upang maging tahimik, hindi ka lang nagpapakalma ng iyong isipan—literal na pinapalago mo ang iyong utak! 

Sa ibang tala, ang pagtawa ay labis na kapaki-pakinabang—hindi lang para sa kaluluwa kundi para rin sa katawan. Maaari itong magpababa ng stress at kahit na magbigay ng boost sa iyong immune system. 

Kaya, sa susunod na tumawa ka, alamin mong hindi ka lang nag-eenjoy; nag-aalaga ka rin sa iyong kalusugan. 

Ngayon, kukuha ako ng isang sandali upang tumingin nang direkta sa kamera at pansamantalang huminto. 

<Pahinga at panatilihing nakasara ang iyong mga labi> 

Habang patuloy kong binabasa ang script na ito, nakadarama ako ng kasiyahan. Hindi na ako makapaghintay na makita ang aking avatar na mabuhay. 

Ibinabahagi ko ang aking mga iniisip, habang ang aking boses ay nananatiling kalmado at ang aking tono ay nananatiling nakakaanyaya. 

Hindi ba nakakabighani kung paano tayo binibigyang-daan ng teknolohiya na lumikha ng mga digital clone? 

Ang aking avatar ay maaaring magamit sa iba't ibang paraan, mula sa pagpapabuti ng komunikasyon hanggang sa pagbibigay ng mga personalized na karanasan. 

Ang avatar na malilikha mula sa footage na ito ay magiging isang makapangyarihang tool para sa iba't ibang aplikasyon. 

Kung ito man ay para sa propesyonal na paggamit, entertainment, o personal na interaksyon, ang digital na bersyon ng aking sarili ay magiging isang mahalagang asset. 

Ang kakayahang palawakin ang aking presensya sa isang digital na mundo ay nagbubukas ng walang katapusang posibilidad para sa komunikasyon at pakikilahok. 

<Pahinga at panatilihing nakasara ang iyong mga labi> 

Excited akong makita kung paano dadalhin ng teknolohiyang ito ang aking digital avatar sa buhay. 

Sa pagsunod sa mga hakbang na ito, makakalikha ako ng isang bagay na makabago at tunay na sumasalamin sa kung sino ako. 

Maghanda nang maranasan ang isang bagay na kamangha-mangha! 

Isipin ang epekto na maaari nating makamit gamit ang makabagong teknolohiyang ito! 

Nasa unahan tayo ng pagbabago, at ang hinaharap ay may walang katapusang posibilidad! 

Ito ay hindi lamang pag-unlad, ito ay isang malaking hakbang pasulong! 

Gumawa tayo ng isang mahusay na Avatar! 

<Pahinga at panatilihing nakasara ang iyong mga labi> 

Hayaan mo rin akong magmuni-muni kung gaano na kalayo ang narating ng teknolohiya. 

Hindi matagal na ang nakalipas, ang paglikha ng mga digital na bersyon ng ating mga sarili ay isang bagay mula sa science fiction. 

Ngayon, ang teknolohiyang ito ay totoo, at ito ay mabilis na lumalaki. Isipin ang isang hinaharap kung saan ang ating mga Avatars ay makakatulong sa atin na malutas ang mga problema. 

Ang mga digital na bersyon ng ating mga sarili ay maaaring higit pa sa mga kopya—maaaring pahusayin nila kung paano tayo nakikisalamuha sa mundo. 

Hindi ba iyon kahanga-hanga? 

Ang paggawa ng isang avatar ay hindi lamang tungkol sa paglikha ng isang digital na bersyon ng ating sarili; ito ay tungkol sa paghubog ng isang hinaharap kung saan ang ating tunay at digital na buhay ay nagtatagpo sa mga kapana-panabik na paraan. 

<Pumreno at panatilihing nakasara ang labi>

Bawat sandali ng recording na ito ay tumutulong sa pagbuo ng aking bagong digital na Avatar.

Isang kapana-panabik na paglalakbay ito, at hindi ko na mahintay kung ano ang susunod. 

At iyon na. Excited akong makita kung paano magiging itsura ng aking Avatar.`,
    portuguese: `<Olhe diretamente para a câmera com uma expressão amigável e natural>

Olá! Hoje, vou ler um roteiro curto para ajudar a criar um avatar digital que me represente com precisão.

Esse avatar servirá como um clone digital, capturando minhas expressões e voz únicas.

Agora, vou ler um roteiro de alguns minutos. A gravação levará 5 minutos.

Por favor, junte-se a mim enquanto criamos meu Avatar!

<Pausa e mantenha os lábios fechados>

Durante esta gravação, manterei um ritmo constante e uma articulação clara. É essencial que eu permaneça natural, relaxado e consistente durante esta leitura para obter o melhor resultado possível.

Isso garante que o Avatar criado será o mais realista e autêntico possível.

Por isso, estou atento aos meus movimentos, usando gestos naturais, garantindo que minhas mãos permaneçam relaxadas e não cubram meu rosto.

Estou me sentindo ótimo lendo este roteiro.

Trago mais expressão aos meus olhos e um sorriso genuíno ao meu rosto. Estou gravando a melhor versão de mim mesmo, o que criará um avatar ainda melhor.

<Pausa e mantenha os lábios fechados>

Deixe-me compartilhar algumas curiosidades fascinantes com você.

Você sabia que a meditação tem demonstrado aumentar o tamanho da matéria cinzenta do seu cérebro?

Então, quando você tira um momento para ficar em silêncio, você não está apenas acalmando sua mente—você está literalmente fazendo seu cérebro crescer!

Por outro lado, o riso é incrivelmente benéfico—não apenas para a alma, mas também para o corpo. Ele pode reduzir o estresse e até mesmo aumentar seu sistema imunológico.

Então, da próxima vez que você rir, saiba que você não está apenas se divertindo; você também está cuidando da sua saúde.

Agora, vou tirar um momento para olhar diretamente para a câmera e pausar brevemente.

<Pausa e mantenha os lábios fechados>

À medida que continuo a ler este roteiro, estou me sentindo empolgado. Mal posso esperar para ver meu avatar ganhar vida.

Estou compartilhando meus pensamentos, enquanto minha voz permanece calma e meu tom convidativo.

Não é fascinante como a tecnologia nos permite criar clones digitais?

Meu avatar pode ser usado de várias maneiras, desde melhorar a comunicação até fornecer experiências personalizadas.

O avatar que será criado a partir desta filmagem será uma ferramenta poderosa para várias aplicações.

Seja para uso profissional, entretenimento ou interações pessoais, esta versão digital de mim mesmo será um ativo valioso.

A capacidade de estender minha presença para um reino digital abre inúmeras possibilidades para comunicação e engajamento.

<Pausa e mantenha os lábios fechados>

Estou animado para ver como esta tecnologia trará meu avatar digital à vida.

Seguindo esses passos, serei capaz de criar algo inovador e verdadeiramente refletivo de quem eu sou.

Prepare-se para experimentar algo incrível!

Imagine o impacto que podemos alcançar com essa tecnologia revolucionária!

Estamos na vanguarda da mudança, e o futuro guarda possibilidades ilimitadas!

Isso não é apenas progresso, é um grande passo à frente!

Vamos fazer um ótimo Avatar!

<Pausa e mantenha os lábios fechados>

Deixe-me também refletir sobre o quão longe a tecnologia chegou.

Não muito tempo atrás, criar versões digitais de nós mesmos era algo saído da ficção científica.

Agora, essa tecnologia é real e está crescendo rapidamente. Imagine um futuro onde nossos Avatares podem nos ajudar a resolver problemas.

Essas versões digitais de nós mesmos podem em breve ser mais do que apenas cópias—they could enhance how we interact with the world.

Não é incrível?

Fazer um avatar não é apenas criar uma versão digital de nós mesmos; é sobre moldar um futuro onde nossas vidas reais e digitais se misturam de maneiras emocionantes.

<Pausa e mantenha os lábios fechados> 

Cada momento desta gravação ajuda a construir o meu novo Avatar digital. 

É uma jornada empolgante e mal posso esperar para ver o que vem a seguir. 

E é isso. Estou animado para ver como ficará o meu Avatar.`,
    turkish: `<Kameraya doğrudan bakarak doğal ve dostça bir ifade takının> 

Merhaba! Bugün, beni doğru bir şekilde temsil eden bir dijital avatar yaratmak için kısa bir senaryo okuyacağım. 

Bu avatar, benzersiz ifadelerimi ve sesimi yakalayan bir dijital kopya olarak hizmet edecek. 

Şimdi birkaç dakikalık bir senaryo okuyacağım. Kaydetmek 5 dakika alacak. 

Lütfen benimle birlikte avatarımı yaratalım! 

<Dur ve dudaklarını kapalı tut> 

Bu kayıt sırasında, sabit bir hızda ve net bir telaffuzla devam edeceğim. En iyi sonuç için doğal, rahat ve tutarlı kalmam çok önemli. 

Bu, oluşturulan Avatar'ın mümkün olduğunca gerçekçi ve otantik olmasını sağlar. 

Bu nedenle, hareketlerime dikkat ediyorum, doğal jestler kullanıyorum, ellerimin rahat kalmasını sağlıyorum ve yüzümü kapatmıyorum. 

Bu senaryoyu okurken harika hissediyorum. 

Gözlerime daha fazla ifade katıyorum ve yüzüme içten bir gülümseme getiriyorum. Kendimin en iyi versiyonunu çekiyorum, bu da daha iyi bir avatar yaratacak. 

<Dur ve dudaklarını kapalı tut> 

Sizinle birkaç ilginç bilgi paylaşayım. 

Meditasyonun, beyninizin gri maddesinin boyutunu gerçekten artırdığını biliyor muydunuz? 

Yani, bir an durup sessiz kalmayı düşündüğünüzde, sadece zihninizi sakinleştirmekle kalmıyor—gerçekten beyninizi büyütüyorsunuz! 

Başka bir açıdan bakıldığında, gülmek son derece faydalıdır—sadece ruh için değil, aynı zamanda beden için de. Stresi azaltabilir ve bağışıklık sisteminizi güçlendirebilir. 

Yani, bir sonraki gülümsemenizde, sadece eğlenmediğinizi bilin; sağlığınıza da dikkat ediyorsunuz. 

Şimdi, kameraya doğrudan bakmak ve kısa bir süre durmak için bir an alacağım. 

<Dur ve dudaklarını kapalı tut> 

Bu senaryoyu okumaya devam ederken, heyecanlı hissediyorum. Avatarımın hayata geçmesini dört gözle bekliyorum. 

Düşüncelerimi paylaşıyorum, sesim sakin kalırken tonum davetkar kalıyor. 

Teknolojinin dijital kopyalar yaratmamıza nasıl izin verdiği ne kadar ilginç değil mi? 

Avatarım, iletişimi geliştirmekten kişiselleştirilmiş deneyimlere kadar çeşitli şekillerde kullanılabilir. 

Bu görüntülerden oluşturulacak avatar, çeşitli uygulamalar için güçlü bir araç olacak. 

İster profesyonel kullanım, ister eğlence ya da kişisel etkileşimler olsun, bu dijital versiyonum değerli bir varlık olacak. 

Dijital bir alanda varlığımı genişletme yeteneği, iletişim ve etkileşim için sayısız olanak açıyor. 

<Dur ve dudaklarını kapalı tut> 

Bu teknolojinin benim dijital avatarımı hayata geçirmesini görmek için heyecanlıyım. 

Bu adımları takip ederek, yenilikçi ve beni gerçek şekilde yansıtan bir şey yaratabileceğim. 

Hazır olun, harika bir şey deneyimleyeceksiniz! 

Bu çığır açan teknoloji ile elde edebileceğimiz etkiyi hayal edin! 

Değişimin ön saflarındayız ve gelecekte sınırsız olanaklar var! 

Bu sadece ilerleme değil, büyük bir adım ileri! 

Harika bir Avatar yapalım! 

<Dur ve dudaklarını kapalı tut> 

Teknolojinin ne kadar ilerlediğini de yansıtmak istiyorum. 

Çok da uzakta olmayan bir zamanda, kendimizin dijital versiyonlarını yaratmak, bilim kurgu hikayelerinden bir şeydi. 

Artık bu teknoloji gerçek ve hızla büyüyor. Avatarlarımızın sorunları çözmemize yardımcı olabileceği bir geleceği hayal edin. 

Bu dijital versiyonlarımız, yalnızca kopya olmaktan daha fazlası olabilir—dünyayla etkileşim şeklimizi geliştirebilirler. 

Bu harika değil mi? 

Bir avatar yaratmak, sadece kendimizin dijital bir versiyonunu oluşturmakla ilgili değil; gerçek ve dijital hayatlarımızın heyecan verici şekillerde bir araya geldiği bir geleceği şekillendirmekle ilgili. 

<Duraklat ve dudaklarını kapalı tut> 

Bu kaydın her anı, yeni dijital Avatar'ımı inşa etmeme yardımcı oluyor.

Heyecan verici bir yolculuk ve sıradaki ne olacak görmek için sabırsızlanıyorum.

Ve hepsi bu kadar. Avatar'ımın nasıl olacağını görmek için heyecanlıyım.`,
    czech: `<Pohleďte přímo do kamery s přirozeným přátelským výrazem> 

Ahoj! Dnes budu číst krátký scénář, abych pomohl vytvořit digitální avatar, který mě přesně reprezentuje. 

Tento avatar bude sloužit jako digitální klon, který zachytí mé jedinečné výrazy a hlas. 

Teď přečtu několik minutový scénář. Nahrávání potrvá 5 minut. 

Prosím, přidejte se ke mně, když vytváříme můj avatar! 

<Pauza a držte rty zavřené> 

Během tohoto nahrávání budu udržovat stálé tempo a jasnou výslovnost. Je nezbytné, abych zůstal přirozený, uvolněný a konzistentní během tohoto čtení, abych dosáhl co nejlepšího výsledku. 

To zajišťuje, že avatar, který bude vytvořen, bude co nejrealističtější a nejautentičtější. 

Takže jsem si vědom svých pohybů, používám přirozené gesta, snažím se, aby moje ruce zůstaly uvolněné a nezakryly mi obličej. 

Při čtení tohoto scénáře se cítím skvěle. 

Přináším více výrazu do svých očí a opravdový úsměv na tvář. Natáčím nejlepší verzi sebe sama, což vytvoří ještě lepší avatar. 

<Pauza a držte rty zavřené> 

Dovolte mi, abych se s vámi podělil o pár fascinujících poznatků. 

Věděli jste, že meditace může skutečně zvýšit velikost šedé hmoty ve vašem mozku? 

Takže když si na chvíli uděláte čas na klid, nezklidňujete jen svou mysl—doslova rostete svůj mozek! 

Na jinou notu, smích je nesmírně prospěšný—nejen pro duši, ale také pro tělo. Může snižovat stres a dokonce posílit váš imunitní systém. 

Takže příště, když se zasmějete, vězte, že se nebavíte jen tak; také se staráte o své zdraví. 

Teď si na chvíli podívám přímo do kamery a krátce se zastavím. 

<Pauza a držte rty zavřené> 

Když pokračuji ve čtení tohoto scénáře, cítím se nadšený. Nemůžu se dočkat, až uvidím, jak se můj avatar oživí. 

Sdílím své myšlenky, zatímco můj hlas zůstává klidný a můj tón zůstává vstřícný. 

Není fascinující, jak nám technologie umožňuje vytvářet digitální klony? 

Můj avatar může být použit různými způsoby, od vylepšení komunikace po poskytování personalizovaných zážitků. 

Avatar, který bude vytvořen z tohoto záznamu, bude mocným nástrojem pro různé aplikace. 

Ať už pro profesionální použití, zábavu nebo osobní interakce, tato digitální verze mě bude cenným aktivem. 

Možnost rozšířit svou přítomnost do digitálního prostoru otevírá nekonečné možnosti pro komunikaci a zapojení. 

<Pauza a držte rty zavřené> 

Jsem nadšený, jak tato technologie oživí mé digitální avatar. 

Dodržováním těchto kroků budu moci vytvořit něco inovativního a skutečně odrazujícího, kým jsem. 

Připravte se na něco neuvěřitelného! 

Představte si dopad, který můžeme dosáhnout s touto revoluční technologií! 

Jsme na pokraji změny a budoucnost má neomezené možnosti! 

Toto není jen pokrok, je to velký krok vpřed! 

Udělejme skvělého avatara! 

<Pauza a držte rty zavřené> 

Dovolte mi také zamyslet se nad tím, jak daleko technologie pokročila. 

Ještě nedávno bylo vytváření digitálních verzí nás něčím z vědeckofantastických příběhů. 

Teď je tato technologie skutečná a rychle roste. Představte si budoucnost, kde mohou naši avatarové pomoci řešit problémy. 

Tyto digitální verze nás by brzy mohly být víc než jen kopie—mohly by vylepšit, jak interagujeme se světem. 

Není to úžasné? 

Vytváření avatara není jen o vytvoření digitální verze nás samých; jde o formování budoucnosti, kde se naše skutečné a digitální životy vzájemně prolínají vzrušujícími způsoby. 

<Pozastavte a držte rty zavřené> 

Každý moment této nahrávky pomáhá vytvořit můj nový digitální Avatar. 

Je to vzrušující cesta a nemohu se dočkat, co přijde dál.

A to je vše. Těším se, jak bude můj Avatar vypadat.`,
    croatian: `<Pogledajte izravno u kameru s prirodnim prijateljskim izrazom> 

Bok! Danas ću pročitati kratak scenarij kako bih pomogao stvoriti digitalni avatar koji me točno predstavlja. 

Ovaj avatar će služiti kao digitalna kopija, hvatajući moje jedinstvene izraze i glas. 

Sada ću pročitati nekoliko minuta scenarija. Snimanje će trajati 5 minuta. 

Molim vas, pridružite mi se dok stvaramo moj avatar! 

<Stanka i držite usne zatvorene> 

Tijekom ovog snimanja održavat ću stalnu brzinu i jasnu artikulaciju. Od esencijalne je važnosti da ostanem prirodan, opušten i dosljedan tijekom ovog čitanja kako bih postigao najbolji mogući rezultat. 

To osigurava da će stvoreni avatar biti što realističniji i autentičniji. 

Dakle, pazim na svoje pokrete, koristeći prirodne geste, osiguravajući da mi ruke ostanu opuštene i da ne pokrivaju lice. 

Osjećam se odlično dok čitam ovaj scenarij. 

Dajem više izraza svojim očima i iskren osmijeh na licu. Snimam najbolju verziju sebe, što će stvoriti još boljeg avatara. 

<Stanka i držite usne zatvorene> 

Dopustite mi da s vama podijelim nekoliko fascinantnih uvida. 

Jeste li znali da je meditacija pokazala da zapravo povećava veličinu sive tvari u vašem mozgu? 

Dakle, kada odvojite trenutak da budete mirni, ne smirujete samo svoju um—doslovno raste vaš mozak! 

Na drugoj strani, smijeh je nevjerojatno koristan—ne samo za dušu već i za tijelo. Može smanjiti stres i čak ojačati vaš imunološki sustav. 

Dakle, sljedeći put kada se nasmijete, znajte da se ne zabavljate samo; također se brinete za svoje zdravlje. 

Sada ću uzeti trenutak da izravno pogledam u kameru i kratko se zaustavim. 

<Stanka i držite usne zatvorene> 

Dok nastavljam čitati ovaj scenarij, osjećam se uzbuđeno. Jedva čekam da vidim kako moj avatar oživljava. 

Dijelim svoje misli, dok moj glas ostaje smiren, a moj ton ostaje prijateljski. 

Nije li fascinantno kako tehnologija omogućava stvaranje digitalnih klonova? 

Moj avatar može se koristiti na različite načine, od poboljšanja komunikacije do pružanja personaliziranih iskustava. 

Avatar koji će biti stvoren iz ovog snimka bit će moćan alat za razne aplikacije. 

Bilo da se radi o profesionalnoj upotrebi, zabavi ili osobnim interakcijama, ova digitalna verzija mene bit će dragocjeno sredstvo. 

Sposobnost proširenja mog prisustva u digitalnom području otvara brojne mogućnosti za komunikaciju i angažman. 

<Stanka i držite usne zatvorene> 

Uzbuđen sam vidjeti kako ova tehnologija donosi moj digitalni avatar u život. 

Prateći ove korake, moći ću stvoriti nešto inovativno i stvarno reflektirajuće tko sam. 

Pripremite se na nešto nevjerojatno! 

Zamislite utjecaj koji možemo postići s ovom revolucionarnom tehnologijom! 

Naša smo na čelu promjena, a budućnost donosi neograničene mogućnosti! 

Ovo nije samo napredak, to je veliki korak naprijed! 

Napravimo sjajan avatar! 

<Stanka i držite usne zatvorene> 

Dopustite mi da se također osvrnem na to koliko je tehnologija napredovala. 

Nedavno, stvaranje digitalnih verzija sebe bilo je nešto iz znanstvene fantastike. 

Sada je ova tehnologija stvarna i brzo raste. Zamislite budućnost u kojoj naši avatari mogu pomoći u rješavanju problema. 

Ove digitalne verzije nas mogli bi uskoro biti više od samo kopija—mogle bi poboljšati kako komuniciramo sa svijetom. 

Nije li to nevjerojatno? 

Stvaranje avatara nije samo o stvaranju digitalne verzije sebe; radi se o oblikovanju budućnosti u kojoj se naši stvarni i digitalni životi stapaju na uzbudljive načine. 

<Pauza i drži usne zatvorene>

Svaki trenutak ove snimke pomaže u izgradnji mog novog digitalnog Avatara.

To je uzbudljivo putovanje i jedva čekam vidjeti što slijedi.

I to je to. Uzbuđen sam vidjeti kako će moj Avatar izgledati.`,
    indonesian: `<Lihat langsung ke kamera dengan ekspresi ramah yang alami> 

Halo! Hari ini, saya akan membaca skrip pendek untuk membantu menciptakan avatar digital yang secara akurat mewakili saya. 

Avatar ini akan berfungsi sebagai salinan digital, menangkap ekspresi dan suara unik saya. 

Saya sekarang akan membaca skrip selama beberapa menit. Ini akan memakan waktu 5 menit untuk merekam. 

Silakan bergabung dengan saya saat kita menciptakan Avatar saya! 

<Berhenti sejenak dan rapatkan bibir Anda> 

Selama perekaman ini, saya akan menjaga kecepatan yang stabil dan artikulasi yang jelas. Sangat penting bagi saya untuk tetap alami, santai, dan konsisten selama pembacaan ini untuk mencapai hasil yang terbaik. 

Ini memastikan bahwa Avatar yang diciptakan akan se-realistis dan se-authentic mungkin. 

Jadi, saya memperhatikan gerakan saya, menggunakan isyarat alami, memastikan tangan saya tetap santai dan tidak menutupi wajah saya. 

Saya merasa hebat saat membaca skrip ini. 

Saya membawa lebih banyak ekspresi ke mata saya dan senyum tulus di wajah saya. Saya merekam versi terbaik dari diri saya, yang akan menciptakan avatar yang bahkan lebih baik. 

<Berhenti sejenak dan rapatkan bibir Anda> 

Izinkan saya membagikan beberapa wawasan menarik kepada Anda. 

Tahukah Anda bahwa meditasi telah terbukti benar-benar meningkatkan ukuran materi abu-abu di otak Anda? 

Jadi, ketika Anda meluangkan waktu untuk tetap tenang, Anda tidak hanya menenangkan pikiran Anda—Anda benar-benar sedang menumbuhkan otak Anda! 

Di sisi lain, tertawa sangat bermanfaat—tidak hanya untuk jiwa tetapi juga untuk tubuh. Itu dapat mengurangi stres dan bahkan meningkatkan sistem kekebalan tubuh Anda. 

Jadi, lain kali Anda tertawa, ketahuilah bahwa Anda tidak hanya bersenang-senang; Anda juga merawat kesehatan Anda. 

Sekarang, saya akan mengambil momen untuk melihat langsung ke kamera dan berhenti sejenak. 

<Berhenti sejenak dan rapatkan bibir Anda> 

Saat saya melanjutkan membaca skrip ini, saya merasa bersemangat. Saya tidak sabar untuk melihat avatar saya hidup. 

Saya membagikan pikiran saya, sementara suara saya tetap tenang dan nada saya tetap mengundang. 

Bukankah menarik bagaimana teknologi memungkinkan kita menciptakan klon digital? 

Avatar saya dapat digunakan dalam berbagai cara, dari meningkatkan komunikasi hingga memberikan pengalaman yang dipersonalisasi. 

Avatar yang akan dibuat dari rekaman ini akan menjadi alat yang kuat untuk berbagai aplikasi. 

Apakah itu untuk penggunaan profesional, hiburan, atau interaksi pribadi, versi digital dari diri saya ini akan menjadi aset yang berharga. 

Kemampuan untuk memperluas kehadiran saya ke dalam ranah digital membuka banyak kemungkinan untuk komunikasi dan keterlibatan. 

<Berhenti sejenak dan rapatkan bibir Anda> 

Saya bersemangat untuk melihat bagaimana teknologi ini menghidupkan avatar digital saya. 

Dengan mengikuti langkah-langkah ini, saya akan dapat menciptakan sesuatu yang inovatif dan benar-benar mencerminkan siapa saya. 

Bersiaplah untuk mengalami sesuatu yang luar biasa! 

Bayangkan dampak yang bisa kita capai dengan teknologi revolusioner ini! 

Kita berada di garis depan perubahan, dan masa depan menyimpan kemungkinan tanpa batas! 

Ini bukan hanya kemajuan, ini adalah langkah besar ke depan! 

Mari kita buat Avatar yang hebat! 

<Berhenti sejenak dan rapatkan bibir Anda> 

Izinkan saya juga merefleksikan seberapa jauh teknologi telah berkembang. 

Tidak lama yang lalu, menciptakan versi digital dari diri kita adalah sesuatu yang keluar dari fiksi ilmiah. 

Sekarang, teknologi ini nyata, dan berkembang dengan cepat. Bayangkan masa depan di mana Avatar kita dapat membantu kita memecahkan masalah. 

Versi digital dari diri kita ini bisa segera lebih dari sekadar salinan—mereka bisa meningkatkan cara kita berinteraksi dengan dunia. 

Bukankah itu luar biasa? 

Membuat avatar bukan hanya tentang menciptakan versi digital dari diri kita; ini tentang membentuk masa depan di mana kehidupan nyata dan digital kita berpadu dengan cara yang menarik. 

<Jeda dan jaga bibir tetap tertutup>

Setiap momen dari rekaman ini membantu membangun Avatar digital baru saya.

Ini adalah perjalanan yang menarik dan saya tidak sabar untuk melihat apa yang akan datang berikutnya.

Dan itu saja. Saya senang melihat bagaimana Avatar saya akan terlihat.`,
    polish: `<Spójrz bezpośrednio w kamerę z naturalnym, przyjaznym wyrazem twarzy> 

Cześć! Dziś przeczytam krótki skrypt, aby pomóc stworzyć cyfrowego awatara, który dokładnie mnie reprezentuje. 

Ten awatar będzie służył jako cyfrowa kopia, uchwytując moje unikalne wyrazy i głos. 

Teraz przeczytam skrypt przez kilka minut. Nagra to 5 minut. 

Proszę, dołącz do mnie, gdy tworzymy mojego Awatara! 

<Zrób pauzę i utrzymaj zamknięte usta> 

Podczas nagrania będę utrzymywać stałe tempo i wyraźną artykulację. To niezwykle ważne, aby pozostać naturalnym, zrelaksowanym i spójnym podczas tego czytania, aby osiągnąć najlepszy możliwy rezultat. 

Zapewnia to, że stworzony Awatar będzie jak najbardziej realistyczny i autentyczny. 

Dlatego zwracam uwagę na moje ruchy, używając naturalnych gestów, upewniając się, że moje ręce pozostają zrelaksowane i nie zakrywają mojej twarzy. 

Czuję się świetnie, czytając ten skrypt. 

Przynoszę więcej ekspresji do moich oczu i szczery uśmiech na moją twarz. Kręcę najlepszą wersję siebie, co stworzy jeszcze lepszego awatara. 

<Zrób pauzę i utrzymaj zamknięte usta> 

Pozwól, że podzielę się kilkoma fascynującymi spostrzeżeniami. 

Czy wiesz, że medytacja wykazała, że rzeczywiście zwiększa rozmiar szarej materii w mózgu? 

Więc, kiedy poświęcasz chwilę na bycie w ciszy, nie tylko uspokajasz swój umysł—dosłownie rozwijasz swój mózg! 

Z innej strony, śmiech jest niesamowicie korzystny—nie tylko dla duszy, ale także dla ciała. Może obniżyć stres i nawet wzmocnić twój system odpornościowy. 

Więc następnym razem, gdy się zaśmiejesz, wiedz, że nie tylko się bawisz; także dbasz o swoje zdrowie. 

Teraz wezmę chwilę, aby spojrzeć bezpośrednio w kamerę i na chwilę się zatrzymać. 

<Zrób pauzę i utrzymaj zamknięte usta> 

Gdy kontynuuję czytanie tego skryptu, czuję się podekscytowany. Nie mogę się doczekać, aby zobaczyć mojego awatara ożywającego. 

Dzielę się swoimi myślami, podczas gdy mój głos pozostaje spokojny, a ton zapraszający. 

Czyż to nie jest fascynujące, jak technologia pozwala nam tworzyć cyfrowe klony? 

Mój awatar może być używany na różne sposoby, od poprawy komunikacji po zapewnienie spersonalizowanych doświadczeń. 

Awatar, który zostanie stworzony z tego nagrania, będzie potężnym narzędziem do różnych zastosowań. 

Niezależnie od tego, czy będzie to do użytku profesjonalnego, rozrywki, czy interakcji osobistych, ta cyfrowa wersja mnie będzie cennym atutem. 

Możliwość przeniesienia mojej obecności do cyfrowej rzeczywistości otwiera niezliczone możliwości komunikacji i zaangażowania. 

<Zrób pauzę i utrzymaj zamknięte usta> 

Cieszę się, że widzę, jak ta technologia ożywia mojego cyfrowego awatara. 

Postępując zgodnie z tymi krokami, będę w stanie stworzyć coś innowacyjnego i naprawdę odzwierciedlającego to, kim jestem. 

Przygotuj się na doświadczenie czegoś niezwykłego! 

Wyobraź sobie wpływ, jaki możemy osiągnąć dzięki tej przełomowej technologii! 

Jesteśmy na czołowej pozycji zmiany, a przyszłość ma nieskończone możliwości! 

To nie tylko postęp, to ogromny krok naprzód! 

Stwórzmy wspaniałego Awatara! 

<Zrób pauzę i utrzymaj zamknięte usta> 

Pozwól, że również refleksyjnie spojrzę, jak daleko zaszła technologia. 

Niedawno tworzenie cyfrowych wersji nas samych było czymś z science fiction. 

Teraz ta technologia jest rzeczywista i szybko się rozwija. Wyobraź sobie przyszłość, w której nasze awatary mogą pomóc nam rozwiązywać problemy. 

Te cyfrowe wersje nas samych mogłyby wkrótce stać się czymś więcej niż tylko kopiami—mogłyby zwiększyć sposób, w jaki interagujemy ze światem. 

Czyż to nie jest niesamowite? 

Tworzenie awatara nie jest tylko tworzeniem cyfrowej wersji nas samych; chodzi o kształtowanie przyszłości, w której nasze prawdziwe i cyfrowe życia łączą się w ekscytujący sposób. 

<Zatrzymaj i trzymaj usta zamknięte>

Każda chwila tego nagrania pomaga zbudować moją nową cyfrową Awatarkę.

To ekscytująca podróż i nie mogę się doczekać, co będzie dalej.

I to wszystko. Jestem podekscytowany, jak będzie wyglądać mój Avatar.`,
    romanian: `<Privește direct în cameră cu o expresie naturală și prietenoasă> 

Salut! Astăzi voi citi un scurt script pentru a ajuta la crearea unui avatar digital care mă reprezintă cu exactitate. 

Acest avatar va servi ca o clonă digitală, capturând expresiile și vocea mea unică. 

Voi citi acum un script de câteva minute. Va dura 5 minute să înregistrez. 

Te rog să te alături mie în timp ce creăm avatarul meu! 

<Fă o pauză și ține buzele închise> 

În timpul acestei înregistrări, voi menține un ritm constant și o articulație clară. Este esențial să rămân natural, relaxat și constant pe parcursul acestei lecturi pentru a obține cel mai bun rezultat posibil. 

Acest lucru asigură că avatarul creat va fi cât mai realist și autentic. 

Așadar, sunt atent la mișcările mele, folosind gesturi naturale, asigurându-mă că mâinile mele rămân relaxate și nu îmi acoperă fața. 

Mă simt grozav citind acest script. 

Adaug mai multă expresie ochilor mei și un zâmbet sincer pe față. Filmând cea mai bună versiune a mea, voi crea un avatar și mai bun. 

<Fă o pauză și ține buzele închise> 

Permite-mi să împărtășesc câteva perspective fascinante cu tine. 

Știai că meditația a arătat că de fapt crește dimensiunea materiei cenușii din creier? 

Așadar, când îți acorzi o clipă de liniște, nu doar că îți calmezi mintea—de fapt, îți crești creierul! 

Pe o notă diferită, râsul este incredibil de benefic—nu doar pentru suflet, ci și pentru corp. Poate reduce stresul și chiar îmbunătăți sistemul tău imunitar. 

Așadar, data viitoare când râzi, știai că nu te distrezi doar; ai grijă și de sănătatea ta. 

Acum, voi lua un moment pentru a privi direct în cameră și a face o pauză scurtă. 

<Fă o pauză și ține buzele închise> 

Pe măsură ce continui să citesc acest script, mă simt entuziasmat. Abia aștept să văd cum prinde viață avatarul meu. 

Împărtășesc gândurile mele, în timp ce vocea mea rămâne calmă și tonul meu rămâne primitor. 

Nu este fascinant cum tehnologia ne permite să creăm clone digitale? 

Avatarul meu poate fi folosit în diferite moduri, de la îmbunătățirea comunicării la furnizarea de experiențe personalizate. 

Avatarul care va fi creat din această înregistrare va fi un instrument puternic pentru diverse aplicații. 

Indiferent dacă este pentru utilizare profesională, divertisment sau interacțiuni personale, această versiune digitală a mea va fi un atu valoros. 

Capacitatea de a extinde prezența mea într-un domeniu digital deschide nenumărate posibilități pentru comunicare și implicare. 

<Fă o pauză și ține buzele închise> 

Sunt entuziasmat să văd cum această tehnologie dă viață avatarului meu digital. 

Urmând acești pași, voi putea crea ceva inovator și cu adevărat reflectiv pentru cine sunt. 

Pregătește-te să experimentezi ceva incredibil! 

Imaginează-ți impactul pe care îl putem avea cu această tehnologie revoluționară! 

Suntem în fruntea schimbării, iar viitorul are posibilități nelimitate! 

Aceasta nu este doar progres, este un pas uriaș înainte! 

Hai să creăm un avatar grozav! 

<Fă o pauză și ține buzele închise> 

Permite-mi, de asemenea, să reflectez asupra cât de departe a ajuns tehnologia. 

Nu cu mult timp în urmă, crearea versiunilor digitale ale noastre era ceva din science fiction. 

Acum, această tehnologie este reală și se dezvoltă rapid. Imaginează-ți un viitor în care avatarele noastre ne pot ajuta să rezolvăm probleme. 

Aceste versiuni digitale ale noastre ar putea curând să fie mai mult decât simple copii—ar putea îmbunătăți modul în care interacționăm cu lumea. 

Nu-i așa că este uimitor? 

Crearea unui avatar nu este doar despre crearea unei versiuni digitale a noastră; este despre modelarea unui viitor în care viețile noastre reale și digitale se îmbină în moduri captivante. 

<Pauză și ține-ți buzele închise>

Fiecare moment al acestei înregistrări ajută la construirea noului meu Avatar digital.

Este o călătorie interesantă și abia aștept să văd ce urmează.

Și asta e tot. Sunt încântat să văd cum va arăta Avatarul meu.`,
    danish: `<Se direkte ind i kameraet med et naturligt og venligt udtryk> 

Hej! I dag vil jeg læse et kort manuskript for at hjælpe med at skabe en digital avatar, der nøjagtigt repræsenterer mig. 

Denne avatar vil fungere som en digital klon, der fanger mine unikke udtryk og stemme. 

Jeg vil nu læse et manuskript på et par minutter. Det vil tage 5 minutter at optage. 

Vær venlig at slutte dig til mig, mens vi skaber min Avatar! 

<Hold en pause og hold dine læber lukkede> 

Under denne optagelse vil jeg opretholde et jævnt tempo og klar artikulation. Det er vigtigt, at jeg forbliver naturlig, afslappet og konsekvent gennem hele denne læsning for at opnå det bedst mulige resultat. 

Dette sikrer, at den Avatar, der bliver skabt, vil være så realistisk og autentisk som muligt. 

Så jeg er opmærksom på mine bevægelser, bruger naturlige gestusser og sørger for, at mine hænder forbliver afslappede og ikke dækker mit ansigt. 

Jeg har det godt med at læse dette manuskript. 

Jeg bringer mere udtryk til mine øjne og et ægte smil til mit ansigt. Jeg optager den bedste version af mig selv, hvilket vil skabe en endnu bedre avatar. 

<Hold en pause og hold dine læber lukkede> 

Lad mig dele et par fascinerende indsigter med dig. 

Vidste du, at meditation faktisk har vist sig at øge størrelsen på hjernens grå substans? 

Så når du tager et øjeblik til at være stille, beroliger du ikke kun dit sind—du vokser bogstaveligt talt din hjerne! 

På en anden note er latter utrolig gavnligt—ikke kun for sjælen, men også for kroppen. Det kan sænke stress og endda styrke dit immunsystem. 

Så næste gang du griner, ved at du ikke kun har det sjovt; du passer også på dit helbred. 

Nu vil jeg tage et øjeblik for at se direkte ind i kameraet og pause kort. 

<Hold en pause og hold dine læber lukkede> 

Mens jeg fortsætter med at læse dette manuskript, føler jeg mig spændt. Jeg kan ikke vente med at se min avatar komme til live. 

Jeg deler mine tanker, mens min stemme forbliver rolig, og min tone forbliver indbydende. 

Er det ikke fascinerende, hvordan teknologien giver os mulighed for at skabe digitale kloner? 

Min avatar kan bruges på mange måder, fra at forbedre kommunikationen til at give personlige oplevelser. 

Den avatar, der vil blive skabt fra dette optagelse, vil være et kraftfuldt værktøj til forskellige applikationer. 

Uanset om det er til professionelt brug, underholdning eller personlige interaktioner, vil denne digitale version af mig selv være en værdifuld ressource. 

Muligheden for at udvide min tilstedeværelse ind i et digitalt rum åbner op for utallige muligheder for kommunikation og engagement. 

<Hold en pause og hold dine læber lukkede> 

Jeg er spændt på at se, hvordan denne teknologi bringer min digitale avatar til live. 

Ved at følge disse trin vil jeg være i stand til at skabe noget innovativt og virkelig reflekterende for, hvem jeg er. 

Gør dig klar til at opleve noget fantastisk! 

Forestil dig den indflydelse, vi kan opnå med denne banebrydende teknologi! 

Vi er i forkant med forandring, og fremtiden rummer ubegribelige muligheder! 

Dette er ikke kun fremskridt, det er et kæmpe skridt fremad! 

Lad os lave en fantastisk Avatar! 

<Hold en pause og hold dine læber lukkede> 

Lad mig også reflektere over, hvor langt teknologien er kommet. 

Ikke for længe siden var det at skabe digitale versioner af os selv noget, der hørte hjemme i science fiction. 

Nu er denne teknologi virkelig, og den vokser hurtigt. Forestil dig en fremtid, hvor vores Avatare kan hjælpe os med at løse problemer. 

Disse digitale versioner af os selv kunne snart være mere end bare kopier—de kunne forbedre, hvordan vi interagerer med verden. 

Er det ikke fantastisk? 

At lave en avatar handler ikke kun om at skabe en digital version af os selv; det handler om at forme en fremtid, hvor vores virkelige og digitale liv smelter sammen på spændende måder. 

<Pause og hold læberne lukkede>

Hvert øjeblik af denne optagelse hjælper med at bygge min nye digitale Avatar.

Det er en spændende rejse, og jeg kan ikke vente med at se, hvad der kommer næste.

Og det er det. Jeg er spændt på at se, hvordan min Avatar bliver.`,
    slovak: `<Pozeraj sa priamo do kamery s prirodzeným priateľským výrazom> 

Ahoj! Dnes budem čítať krátky skript, aby som pomohol vytvoriť digitálnu avatar, ktorý presne reprezentuje mňa. 

Tento avatar bude slúžiť ako digitálna kópia, zachytávajúca moje jedinečné výrazy a hlas. 

Teraz prečítam niekoľko minútový skript. Nahrávanie potrvá 5 minút. 

Prosím, pripojte sa ku mne, keď vytvárame môj Avatar! 

<Zastav sa a maj pery zatvorené> 

Počas tejto nahrávky si zachovám stabilné tempo a jasnú artikuláciu. Je nevyhnutné, aby som zostal prirodzený, uvoľnený a konzistentný počas celého tohto čítania, aby som dosiahol čo najlepší výsledok. 

To zabezpečuje, že avatar, ktorý bude vytvorený, bude čo najrealistickejší a najautentickejší. 

Takže som si vedomý svojich pohybov, používam prirodzené gestá a zabezpečujem, aby moje ruky zostali uvoľnené a nezakrývali moju tvár. 

Cítim sa skvele, keď prechádzam týmto skriptom. 

Prinášam viac výrazu do svojich očí a úprimný úsmev na svoju tvár. Natáčam najlepšiu verziu seba, čo vytvorí ešte lepšieho avatara. 

<Zastav sa a maj pery zatvorené> 

Dovoľte mi zdieľať s vami niekoľko fascinujúcich postrehov. 

Vedeli ste, že meditácia preukázala, že skutočne zvyšuje veľkosť sivej hmoty vášho mozgu? 

Takže, keď si na chvíľu oddýchnete, nielenže upokojujete svoju myseľ—doslova rastiete svoj mozog! 

Na inej note, smiech je nesmierne prospešný—nie len pre dušu, ale aj pre telo. Môže znižovať stres a dokonca posilňovať váš imunitný systém. 

Takže, nabudúce, keď sa zasmějete, vedzte, že sa nielenže bavíte; taktiež sa staráte o svoje zdravie. 

Teraz si na chvíľu pozriem priamo do kamery a krátko sa pozastavím. 

<Zastav sa a maj pery zatvorené> 

Keď pokračujem v čítaní tohto skriptu, cítim sa nadšene. Nemôžem sa dočkať, kedy uvidím svoj avatar ožiť. 

Zdieľam svoje myšlienky, pričom môj hlas zostáva pokojný a môj tón zostáva pozývajúci. 

Nie je fascinujúce, ako technológia umožňuje vytvárať digitálne klony? 

Môj avatar môže byť použitý rôznymi spôsobmi, od zlepšovania komunikácie po poskytovanie personalizovaných zážitkov. 

Avatar, ktorý bude vytvorený z týchto záberov, bude silným nástrojom pre rôzne aplikácie. 

Či už na profesionálne využitie, zábavu alebo osobné interakcie, táto digitálna verzia mňa bude cenným prínosom. 

Možnosť predĺžiť svoju prítomnosť do digitálnej sféry otvára nespočetné možnosti pre komunikáciu a angažovanosť. 

<Zastav sa a maj pery zatvorené> 

Som nadšený, aby som videl, ako táto technológia oživuje moju digitálnu avatar. 

Nasledovaním týchto krokov budem schopný vytvoriť niečo inovatívne a skutočne odrážajúce to, kým som. 

Pripravte sa na to, aby ste zažili niečo neuveriteľné! 

Predstavte si dopad, ktorý môžeme dosiahnuť s touto revolučnou technológiou! 

Sme na čele zmien, a budúcnosť prináša neobmedzené možnosti! 

Toto nie je len pokrok, je to obrovský krok vpred! 

Poďme vytvoriť skvelého avatara! 

<Zastav sa a maj pery zatvorené> 

Dovoľte mi tiež zamyslieť sa nad tým, ako ďaleko technológia pokročila. 

Nedávno vytváranie digitálnych verzií seba samého bolo niečo, čo patrilo do sci-fi. 

Teraz je táto technológia skutočná a rýchlo rastie. Predstavte si budúcnosť, kde naše Avatare môžu pomôcť vyriešiť problémy. 

Tieto digitálne verzie nás samých by čoskoro mohli byť viac než len kópie—mohli by zlepšiť, ako interagujeme so svetom. 

Nie je to úžasné? 

Vytváranie avatara nie je len o vytváraní digitálnej verzie seba samého; je to o formovaní budúcnosti, kde sa naše skutočné a digitálne životy spájajú vzrušujúcimi spôsobmi. 

<Pauza a držte pery zatvorené>

Každý okamih tohto nahrávania pomáha vytvoriť môj nový digitálny Avatar.

Je to vzrušujúca cesta a neviem sa dočkať, čo bude nasledovať.

A to je všetko. Teším sa, ako bude môj Avatar vyzerať.`,
    malay: `<Lihat terus ke kamera dengan ekspresi mesra yang semula jadi> 

Halo! Hari ini, saya akan membaca skrip pendek untuk membantu membuat avatar digital yang menggambarkan diri saya dengan tepat. 

Avatar ini akan berfungsi sebagai klon digital, menangkap ekspresi dan suara unik saya. 

Saya sekarang akan membaca skrip selama beberapa minit. Ia akan mengambil masa 5 minit untuk merakam. 

Sila sertai saya sambil kita mencipta Avatar saya! 

<Berhenti sejenak dan rapatkan bibir> 

Semasa rakaman ini, saya akan mengekalkan rentak yang stabil dan artikulasi yang jelas. Sangat penting bagi saya untuk tetap semula jadi, santai, dan konsisten sepanjang bacaan ini untuk mencapai hasil yang terbaik. 

Ini memastikan bahawa Avatar yang dihasilkan akan serealistik dan autentik mungkin. 

Jadi, saya sedar tentang gerakan saya, menggunakan isyarat yang semula jadi, memastikan tangan saya tetap santai dan tidak menutupi wajah saya. 

Saya berasa hebat membaca skrip ini. 

Saya menambahkan lebih banyak ekspresi ke dalam mata saya dan senyuman tulus ke wajah saya. Saya merakam versi terbaik diri saya, yang akan mencipta avatar yang lebih baik lagi. 

<Berhenti sejenak dan rapatkan bibir> 

Biarkan saya berkongsi beberapa pandangan menarik dengan anda. 

Adakah anda tahu bahawa meditasi terbukti dapat meningkatkan ukuran materi kelabu otak anda? 

Jadi, apabila anda mengambil sedikit masa untuk tenang, anda bukan hanya menenangkan fikiran anda—anda sebenarnya sedang membesarkan otak anda! 

Di sisi lain, ketawa sangat bermanfaat—bukan sahaja untuk jiwa tetapi juga untuk badan. Ia boleh mengurangkan tekanan dan bahkan meningkatkan sistem imun anda. 

Jadi, lain kali anda ketawa, ketahuilah bahawa anda bukan hanya bersenang-senang; anda juga menjaga kesihatan anda. 

Sekarang, saya akan mengambil sedikit masa untuk melihat langsung ke kamera dan berhenti sekejap. 

<Berhenti sejenak dan rapatkan bibir> 

Semasa saya terus membaca skrip ini, saya merasa teruja. Saya tidak sabar untuk melihat avatar saya hidup. 

Saya berkongsi pemikiran saya, sementara suara saya tetap tenang dan nada saya tetap mengundang. 

Bukankah menarik bagaimana teknologi membolehkan kita mencipta klon digital? 

Avatar saya boleh digunakan dalam pelbagai cara, dari meningkatkan komunikasi hingga memberikan pengalaman yang diperibadikan. 

Avatar yang akan dihasilkan daripada rakaman ini akan menjadi alat yang kuat untuk pelbagai aplikasi. 

Sama ada untuk penggunaan profesional, hiburan, atau interaksi peribadi, versi digital diri saya ini akan menjadi aset yang berharga. 

Kemampuan untuk memperluas kehadiran saya ke dalam dunia digital membuka pelbagai kemungkinan untuk komunikasi dan penglibatan. 

<Berhenti sejenak dan rapatkan bibir> 

Saya teruja untuk melihat bagaimana teknologi ini membawa avatar digital saya hidup. 

Dengan mengikuti langkah-langkah ini, saya akan dapat mencipta sesuatu yang inovatif dan benar-benar mencerminkan siapa saya. 

Bersiaplah untuk mengalami sesuatu yang luar biasa! 

Bayangkan impak yang dapat kita capai dengan teknologi revolusioner ini! 

Kita berada di barisan hadapan perubahan, dan masa depan mempunyai kemungkinan tanpa batas! 

Ini bukan hanya kemajuan, ia adalah langkah besar ke hadapan! 

Mari kita buat Avatar yang hebat! 

<Berhenti sejenak dan rapatkan bibir> 

Biarkan saya juga merenungkan betapa jauh teknologinya telah berkembang. 

Tidak lama dahulu, mencipta versi digital diri kita adalah sesuatu dari fiksyen sains. 

Kini, teknologi ini adalah nyata, dan ia berkembang dengan pesat. Bayangkan masa depan di mana Avatar kita dapat membantu kita menyelesaikan masalah. 

Versi digital diri kita ini mungkin tidak lama lagi lebih dari sekadar salinan—mereka boleh meningkatkan cara kita berinteraksi dengan dunia. 

Bukankah itu menakjubkan? 

Membuat avatar bukan hanya tentang mencipta versi digital diri kita; ia adalah tentang membentuk masa depan di mana kehidupan sebenar dan digital kita bercampur dengan cara yang menarik. 

<Jeda dan pastikan bibir ditutup>

Setiap detik rakaman ini membantu membina Avatar digital baru saya.

Ini adalah perjalanan yang menarik dan saya tidak sabar untuk melihat apa yang akan datang seterusnya.

Dan itu sahaja. Saya teruja untuk melihat bagaimana Avatar saya akan kelihatan.`,
};

export const avatarTextByLanguage: Record<AvatarType, Record<SupportedLanguage, string>> = {
    [AvatarType.Express]: expressAvatarTextByLanguage,
    [AvatarType.PremiumPlus]: premiumPlusAvatarTextByLanguage,
};
export const rtlLanguages = ['arabic'];
export const displayLanguages = [
    'english',
    'spanish',
    'italian',
    'swedish',
    'dutch',
    'german',
    'french',
    'filipino',
    'portuguese',
    'turkish',
    'czech',
    'croatian',
    'indonesian',
    'polish',
    'romanian',
    'danish',
    'slovak',
    'malay',
];
